

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { usePermission } from '../../auth/usePermission';
import { useTravelUi } from '../../hooks/useTravelUi';
import {
  Card,
  Grid,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  DialogTitle,
  DialogContentText,
  Modal,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataLoadingSpinner from "components/Spinners/DataLoadingSpinner";
import colors from "assets/theme/base/colors";
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MDButton from "components/MDButton";
import EmailIcon from '@mui/icons-material/Email';
import PaymentIcon from '@mui/icons-material/Payment';
import EmailModal from 'components/EmailModal';
import SendPaymentLinkModal from 'components/SendPaymentLinkModal';
import PaymentScheduleList from 'components/PaymentScheduleList';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useSnackbar } from 'notistack';
import AttachmentIcon from '@mui/icons-material/Attachment';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

import AttachmentUploadModal from 'components/CustomModals/AttachmentUploadModal';
import Attachments from 'layouts/dashboard/components/Attachments';
import CancelIcon from '@mui/icons-material/Cancel';
import { downloadPrivateObject, uploadPrivateObject } from 'utils/S3';
import { formatDate } from 'utils/DateTimeConversion';
import { useTheme } from '@mui/material/styles';
import { serializeEmailAttachments } from 'utils/emailAttachments';
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import { useCurrency } from '../../context/CurrencyContext';
import { frontendLogger } from '../../utils/logger';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplyIcon from '@mui/icons-material/Reply';
import CreateIcon from '@mui/icons-material/Create';
import MDUserAvatar from 'components/MDUserAvatar';
import EchoComposer from 'components/EchoComposer/EchoComposer';
import RecordPageV2, { TabStack } from "components/v2/record/RecordPageV2";
import { HeroBadge } from "components/v2/record/RecordHero";
import { StatusBadge } from "layouts/crm/account360/RelatedList";
import ActivityTimeline from "layouts/crm/account360/ActivityTimeline";
import { relativeTime } from "layouts/crm/account360/format";
import { Building2, CalendarClock, CalendarDays, CheckSquare, CircleDollarSign, CircleDot, History, LayoutGrid, Package, Paperclip, Receipt, Wallet } from "lucide-react";

const auditFields = [
  { label: "Created By", field: "created_by_name", userIdField: "created_by" },
  { label: "Modified By", field: "updated_by_name", userIdField: "updated_by" },
  { label: "Created Date", field: "created_at", readOnly: true },
  { label: "Modified Date", field: "updated_at", readOnly: true },
];

// Add this utility function near the top of the file, after the imports
const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

function InvoiceDetail() {
  const { id } = useParams();
  const { user, axiosInstance } = useAuth();
  const { isSupportUser } = usePermission('invoices');
  // UI v2 record layout (design-reference "Sales · Deal record").
  const isV2 = useTheme()?.pearlUi === 'v2';
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [order, setOrder] = useState(null); // <-- Add order state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadLoadingId, setDownloadLoadingId] = useState(null);
  const [openEditInvoiceModal, setOpenEditInvoiceModal] = useState(false);
  const [editedInvoice, setEditedInvoice] = useState(null);
  const [editedInvoiceItems, setEditedInvoiceItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [productList, setProductList] = useState([]);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState({ to: '', cc: '', bcc: '', subject: '', content: '' });
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [emailContext, setEmailContext] = useState('');
  const [currentAttachment, setCurrentAttachment] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isGstDialogOpen, setIsGstDialogOpen] = useState(false);
  const [isEwayBillChecked, setIsEwayBillChecked] = useState(false);
  const [isGstGenerating, setIsGstGenerating] = useState(false);
  const [isGstDialogGenerating, setIsGstDialogGenerating] = useState(false);
  const [hasIRN, setHasIRN] = useState(false);
  const previousGstStatus = useRef(null);
  const [customFields, setCustomFields] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const customFieldsRef = useRef(null);
  const { formatCurrency, currency: systemCurrency, getCurrencyLabel } = useCurrency();
  const [transdocdate, setTransdocdate] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(''); // Assuming you have this state for invoice date
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [gstIrn, setGstIrn] = useState(null);
  // 1. Add state for section preferences
  const [sectionOrder, setSectionOrder] = useState([]);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(true);
  // Travel Information is travel-only; Payment Schedule stays for all tenants
  // (Stripe/Razorpay installments + resend are not travel-specific).
  const { showTravel: isTravelEnabled, loading: travelUiLoading } = useTravelUi();
  const visibleSections = sectionOrder.filter(
    (key) =>
      !hiddenSections.includes(key)
      && (isTravelEnabled || key !== 'Travel Information'),
  );

  // Add state for inline editing of unit prices
  const [editingPriceIndex, setEditingPriceIndex] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);

  // Echo feed state variables
  const [feedItems, setFeedItems] = useState([]);
  const [isEchoModalOpen, setIsEchoModalOpen] = useState(false);
  const [editingFeedItem, setEditingFeedItem] = useState(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [newEchoContent, setNewEchoContent] = useState('');
  const [isFeedLoading, setIsFeedLoading] = useState(false);
  const [editFeedContent, setEditFeedContent] = useState('');
  const [isEditingFeed, setIsEditingFeed] = useState(false);
  const entityType = 'invoices'; // Add this constant
  const [nameCache, setNameCache] = useState({}); // Add nameCache state
  // A travel invoice needs the TRAVEL edition and the company travel toggle.
  // (isTravelEnabled / travelUiLoading come from useTravelUi above with section prefs.)
  const [travelOrderDetail, setTravelOrderDetail] = useState(null);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [isPaymentLinkModalOpen, setIsPaymentLinkModalOpen] = useState(false);
  const [paymentLinkInvoiceId, setPaymentLinkInvoiceId] = useState(null);
  const [paymentLinkModalTitle, setPaymentLinkModalTitle] = useState('Send payment link');
  const [gatewayAvailability, setGatewayAvailability] = useState(null);

  const openPaymentLinkModal = useCallback((targetInvoiceId, title = 'Send payment link') => {
    setPaymentLinkInvoiceId(targetInvoiceId != null ? Number(targetInvoiceId) : Number(id));
    setPaymentLinkModalTitle(title);
    setIsPaymentLinkModalOpen(true);
  }, [id]);

  const closePaymentLinkModal = useCallback(() => {
    setIsPaymentLinkModalOpen(false);
    setPaymentLinkInvoiceId(null);
    setPaymentLinkModalTitle('Send payment link');
  }, []);

  const fetchFeedItems = useCallback(async () => {
    try {
      setIsFeedLoading(true);
      const response = await axiosInstance.get(`/feed-items/entity/${entityType}/${id}`);
      setFeedItems(response.data || []);
    } catch (error) {
      console.error('Error fetching feed items:', error);
      setFeedItems([]);
    } finally {
      setIsFeedLoading(false);
    }
  }, [id]);
 

  // Fetch feed items on component mount
  useEffect(() => {
    if (id) {
      fetchFeedItems();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchInvoiceDetail();
    }
  }, [id]);

  useEffect(() => {
    const fetchGatewayAvailability = async () => {
      try {
        const res = await axiosInstance.get('/payment-gateways/availability');
        setGatewayAvailability(res.data || null);
      } catch (err) {
        setGatewayAvailability(null);
      }
    };
    fetchGatewayAvailability();
  }, [axiosInstance]);
  // Memoize formatting functions to prevent unnecessary re-renders
  const formatIndianNumber = useCallback((number) => {
    if (isNaN(number) || number === null || number === undefined) return "0.00";
    const [integerPart, decimalPart] = Number(number).toFixed(2).split(".");
    const lastThreeDigits = integerPart.slice(-3);
    const otherDigits = integerPart.slice(0, -3);
    let formattedNumber = "";
    if (otherDigits) {
      formattedNumber = otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThreeDigits;
    } else {
      formattedNumber = lastThreeDigits;
    }
    return `${formattedNumber}.${decimalPart}`;
  }, []);

  // Use useCallback for fetch function to prevent unnecessary re-renders
  const fetchInvoiceDetail = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/invoices/${id}/details/`);
      // Debug: Log the full API response
      console.log('[InvoiceDetail] /invoices/{id}/details/ response:', response.data);
      // Debug: Log the fields used for invoice items
      console.log('[InvoiceDetail] response.data.items:', response.data.items);
      console.log('[InvoiceDetail] response.data.invoice_items:', response.data.invoice_items);
      // Debug: Log subtotal and total_amount values
      const invoiceData = response.data.invoice || response.data;
      console.log('[InvoiceDetail] Subtotal:', invoiceData.subtotal);
      console.log('[InvoiceDetail] Total Amount:', invoiceData.total_amount);
      console.log('[InvoiceDetail] Are they equal?', invoiceData.subtotal === invoiceData.total_amount);
      setInvoice(response.data.invoice || response.data);
      setInvoiceItems(response.data.items || response.data.invoice_items || []);
      // Debug: Log the final invoiceItems state
      setTimeout(() => {
        console.log('[InvoiceDetail] Final invoiceItems state:', response.data.items || response.data.invoice_items || []);
      }, 0);
      // Fetch travel order details for travel-linked invoices
      if (invoiceData.travel_order_id) {
        try {
          const travelOrderResp = await axiosInstance.get(`/travel-orders/${invoiceData.travel_order_id}/detail`);
          setTravelOrderDetail(travelOrderResp.data || null);
        } catch (err) {
          setTravelOrderDetail(null);
        }
      } else {
        setTravelOrderDetail(null);
      }
      try {
        const scheduleResp = await axiosInstance.get(`/payment-gateways/invoices/${id}/payment-schedule`);
        setPaymentSchedule(scheduleResp.data || []);
      } catch (err) {
        setPaymentSchedule([]);
      }
      // Fetch legacy sales order breakdown if sales_order_id exists
      const orderId = invoiceData.sales_order_id || invoiceData.order_id;
      if (orderId) {
        try {
          const orderResp = await axiosInstance.get(`/orders/${orderId}`);
          setOrder(orderResp.data);
        } catch (err) {
          setOrder(null);
        }
      } else {
        setOrder(null);
      }
    } catch (err) {
      setError('Failed to fetch invoice details');
      console.error('Error fetching invoice details:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // useEffect(() => {
  //   fetchInvoiceDetail();
  // }, [axiosInstance]);

  // Fetch accounts and products for edit modal
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await axiosInstance.get('/accounts/');
        const formattedAccounts = response.data.map(account => ({
          value: account.id,
          label: account.name || `Account ${account.id}`,
          fullData: account,
          currency: account.currency
        }));
        setAccounts(formattedAccounts);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };
    const fetchProducts = async () => {
      try {
        const response = await axiosInstance.get('/products/');
        setProductList(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchAccounts();
    fetchProducts();
  }, [openEditInvoiceModal]);

  // Add useEffect to check IRN status
  useEffect(() => {
    const checkIRNStatus = async () => {
      try {
        const response = await axiosInstance.get(`/gst-invoices/${id}`);
        setHasIRN(!!(response.data?.irn || response.data?.Irn));
      } catch (error) {
        // If 404, means no GST invoice exists
        if (error.response?.status === 404) {
          setHasIRN(false);
        }
      }
    };

    if (id) {
      checkIRNStatus();
    }
  }, [id]);

  // Add useEffect to fetch custom fields
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get(`/custom-fields/invoices`);
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };

    fetchCustomFields();
  }, []);

  useEffect(() => {
    const fetchGstIrn = async () => {
      try {
        if (!invoice?.id) return;
        const response = await axiosInstance.get(`/gst-invoices/${invoice.id}`);
        setGstIrn(response.data?.irn || null);
      } catch (error) {
        setGstIrn(null);
      }
    };
    fetchGstIrn();
  }, [invoice?.id]);

  // 2. Fetch section preferences on mount and on tab focus
  useEffect(() => {
    const fetchSectionPreferences = async () => {
      try {
        const res = await axiosInstance.get('/preferences/global');
        const prefs = res.data?.section_preferences?.invoice || { order: [], hidden: [] };
        const renameSection = (s) => (s === 'Transport Details' ? 'Shipping Details' : s);
        const defaultOrder = [
          'Invoice Information',
          'Travel Information',
          'Shipping Details',
          'Invoice Items',
          'Payment Schedule',
          'Audit Information',
        ];
        let order = Array.isArray(prefs.order) && prefs.order.length > 0
          ? prefs.order.map(renameSection)
          : defaultOrder;
        // Older prefs omit Payment Schedule — insert before Audit when missing.
        if (!order.includes('Payment Schedule')) {
          const auditIdx = order.indexOf('Audit Information');
          order = [...order];
          order.splice(auditIdx >= 0 ? auditIdx : order.length, 0, 'Payment Schedule');
        }
        setSectionOrder(order);
        setHiddenSections(Array.isArray(prefs.hidden) ? prefs.hidden.map(renameSection) : []);
      } catch (e) {
        setSectionOrder([
          'Invoice Information',
          'Travel Information',
          'Shipping Details',
          'Invoice Items',
          'Payment Schedule',
          'Audit Information',
        ]);
        setHiddenSections([]);
      } finally {
        setLoadingSections(false);
      }
    };

    fetchSectionPreferences();

    // Refetch on tab focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSectionPreferences();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Prevent editing invoices
  const handleEditInvoice = async (invoiceId) => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to edit invoices.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    setIsEditLoading(true);
    try {
      // Check if GST e-invoice exists
      const gstResponse = await axiosInstance.get(`/gst-invoices/${id}`);
      if (gstResponse.data && (gstResponse.data.irn || gstResponse.data.Irn)) {
        enqueueSnackbar("Cannot edit invoice after GST e-invoice generation", { variant: "error" });
        return;
      }

      // Fetch full invoice details
      const invoiceResponse = await axiosInstance.get(`/invoices/${id}/`);
      // Fetch invoice items
      const invoiceItemsResponse = await axiosInstance.get(`/invoices/${id}/items/`);
      const today = new Date().toISOString().split('T')[0];
      const transdocdate = invoiceResponse.data.transdocdate
        ? invoiceResponse.data.transdocdate.split('T')[0]
        : today;

      setEditedInvoice({
        ...invoiceResponse.data,
        invoice_date: invoiceResponse.data.invoice_date
          ? new Date(invoiceResponse.data.invoice_date).toISOString().split('T')[0]
          : null,
        due_date: invoiceResponse.data.due_date
          ? new Date(invoiceResponse.data.due_date).toISOString().split('T')[0]
          : null,
        transdocdate: transdocdate,
        transdocno: invoiceResponse.data.transdocno || '',
        transid: invoiceResponse.data.transid || '',
        transname: invoiceResponse.data.transname || '',
        transmode: invoiceResponse.data.transmode || '',
        vehno: invoiceResponse.data.vehno || '',
        vehtype: invoiceResponse.data.vehtype || ''
      });
      setEditedInvoiceItems(
        invoiceItemsResponse.data.map(item => ({
          ...item,
          markedForDeletion: false,
          tempId: item.id ? item.id : Date.now() + Math.random()
        }))
      );
      setOpenEditInvoiceModal(true);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      if (error.response?.status === 404) {
        // If GST e-invoice endpoint returns 404, it means no GST e-invoice exists, so we can proceed
        try {
          // Fetch full invoice details
          const invoiceResponse = await axiosInstance.get(`/invoices/${id}/`);
          // Fetch invoice items
          const invoiceItemsResponse = await axiosInstance.get(`/invoices/${id}/items/`);
          setEditedInvoice({
            ...invoiceResponse.data,
            invoice_date: invoiceResponse.data.invoice_date
              ? new Date(invoiceResponse.data.invoice_date).toISOString().split('T')[0]
              : null,
            due_date: invoiceResponse.data.due_date
              ? new Date(invoiceResponse.data.due_date).toISOString().split('T')[0]
              : null,
            transdocdate: invoiceResponse.data.transdocdate // Ensure this is set
          });
          setEditedInvoiceItems(
            invoiceItemsResponse.data.map(item => ({
              ...item,
              markedForDeletion: false,
              tempId: item.id ? item.id : Date.now() + Math.random()
            }))
          );
          setOpenEditInvoiceModal(true);
        } catch (fetchError) {
          console.error("Error fetching invoice details:", fetchError);
          enqueueSnackbar("Error fetching invoice details", { variant: "error" });
        }
      } else {
        enqueueSnackbar("Error checking GST e-invoice status", { variant: "error" });
      }
    } finally {
      setIsEditLoading(false);
    }
  };

  // Handlers for editing invoice fields/items (copied from Invoices.js)
  const handleInvoiceFieldChange = (field, value) => {
    if (field === 'transdocdate') {
      const today = new Date().toISOString().split('T')[0];
      if (value !== today) {
        setSnackbar({
          open: true,
          message: 'Transport document date must be today',
          severity: 'error'
        });
        return; // Don't update the field
      }
    }
    setEditedInvoice(prev => {
      const updates = { ...prev, [field]: value === '' ? null : value };
      if (field === 'account_id') {
        const selectedAccount = accounts.find(acc => acc.value === value);
        if (selectedAccount) {
          updates.account_name = selectedAccount.label;
          updates.currency = selectedAccount.currency;
        }
      }
      return updates;
    });
  };

  const handleInvoiceItemChange = (tempId, field, value) => {
    const updatedItems = [...editedInvoiceItems];
    const itemIndex = updatedItems.findIndex(item => item.tempId === tempId);
    if (itemIndex === -1) return;
    const item = updatedItems[itemIndex];
    if (field === "discount_percentage") {
      if (value > 100) value = 100;
      else if (value < 0) value = 0;
    }
    if (field === 'product_name') {
      const selectedProduct = productList.find(p => p.name === value);
      if (selectedProduct) {
        item.unit_price = selectedProduct.price;
        item.tax = selectedProduct.tax_rate;
        item.product_id = selectedProduct.id;
      }
    }
    item[field] = field === 'product_name' ? value : (value === '' ? 0 : parseFloat(value));
    const quantity = parseFloat(item.quantity) || 0;
    const discountedPrice = item.unit_price - (item.unit_price * (item.discount_percentage / 100));
    item.discounted_price = discountedPrice;
    const totalNetPrice = discountedPrice * quantity;
    item.total_price = totalNetPrice * (1 + (item.tax / 100));
    setEditedInvoiceItems(updatedItems);
  };
  const handleAddInvoiceItem = () => {
    const newItem = {
      product_name: '',
      unit_price: 0,
      quantity: 1,
      discount_percentage: 0,
      tax: 0,
      total_price: 0,
      discounted_price: 0,
      markedForDeletion: false,
      tempId: Date.now() + Math.random()
    };
    setEditedInvoiceItems(prev => [...prev, newItem]);
  };
  const handleRemoveInvoiceItem = (tempId) => {
    const activeItems = editedInvoiceItems.filter(item => !item.markedForDeletion);
    if (activeItems.length <= 1) return;
    const updatedItems = [...editedInvoiceItems];
    const itemIndex = updatedItems.findIndex(item => item.tempId === tempId);
    if (itemIndex === -1) return;
    const itemToRemove = updatedItems[itemIndex];
    if (itemToRemove.id) {
      updatedItems[itemIndex] = { ...itemToRemove, markedForDeletion: true };
    } else {
      updatedItems.splice(itemIndex, 1);
    }
    setEditedInvoiceItems(updatedItems);
  };
  // Calculate totals (copied from Invoices.js)
  const calculateTotalAmount = (items = editedInvoiceItems) => {
    let subtotal = 0;
    let taxSummary = {};
    const itemsToCalculate = items || editedInvoiceItems;
    itemsToCalculate.forEach((item) => {
      if (item.markedForDeletion) return;
      const unitPrice = parseFloat(item.unit_price) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const discountPercentage = parseFloat(item.discount_percentage) || 0;
      const taxRate = parseFloat(item.tax) || 0;
      const discountedPrice = unitPrice - (unitPrice * (discountPercentage / 100));
      const totalDiscountedPrice = discountedPrice * quantity;
      subtotal += totalDiscountedPrice;
      // Tax on discounted net (after discount).
      const taxAmount = totalDiscountedPrice * (taxRate / 100);
      if (taxRate) {
        taxSummary[taxRate] = (taxSummary[taxRate] || 0) + taxAmount;
      }
      item.discounted_price = discountedPrice;
      item.total_discounted_price = totalDiscountedPrice;
      item.tax_amount = taxAmount;
      item.total_price = totalDiscountedPrice + taxAmount;
    });
    const totalTax = Object.values(taxSummary).reduce((acc, taxAmount) => acc + taxAmount, 0);
    const grandTotal = subtotal + totalTax;
    return { subtotal, grandTotal, taxSummary };
  };
  // Save handler (copied from Invoices.js)
  const handleSaveInvoice = async () => {
    try {
      setIsLoading(true);
      const validItems = editedInvoiceItems.filter(item =>
        !item.markedForDeletion &&
        item.product_name &&
        item.product_name.trim() !== ''
      );
      if (validItems.length === 0) {
        setSnackbar({
          open: true,
          message: 'At least one valid item is required',
          severity: 'error'
        });
        setIsLoading(false);
        return;
      }

      if (editedInvoice?.invoice_date && editedInvoice?.due_date) {
        const invoiceDate = new Date(editedInvoice.invoice_date);
        const dueDate = new Date(editedInvoice.due_date);
        if (dueDate < invoiceDate) {
          setSnackbar({
            open: true,
            message: 'Due date cannot be earlier than invoice date',
            severity: 'error'
          });
          setIsLoading(false);
          return;
        }
      }

      const invalidQtyItem = validItems.find((item) => {
        const qty = parseFloat(item.quantity);
        return Number.isNaN(qty) || qty <= 0;
      });
      if (invalidQtyItem) {
        setSnackbar({
          open: true,
          message: 'Quantity must be greater than 0 for all line items',
          severity: 'error'
        });
        setIsLoading(false);
        return;
      }

      const { subtotal, grandTotal, taxSummary } = calculateTotalAmount(validItems);
      const taxSlabs = Object.entries(taxSummary).map(([rate, amount]) => ({
        tax_rate: parseFloat(rate),
        tax_amount: parseFloat(amount.toFixed(2))
      }));
      const transdocdate = editedInvoice.transdocdate === '' ? null : editedInvoice.transdocdate;
      const invoicePayload = {
        id: editedInvoice.id,
        invoice_number: editedInvoice.invoice_number,
        invoice_date: editedInvoice.invoice_date,
        due_date: editedInvoice.due_date,
        status: editedInvoice.status,
        notes: editedInvoice.notes || '',
        total_amount: parseFloat(grandTotal.toFixed(2)),
        subtotal: parseFloat(subtotal.toFixed(2)),
        account_id: editedInvoice.account_id,
        account_name: editedInvoice.account_name,
        currency: editedInvoice.currency,
        transid: editedInvoice.transid || '',
        transname: editedInvoice.transname || '',
        transmode: editedInvoice.transmode || '',
        vehno: editedInvoice.vehno || '',
        vehtype: editedInvoice.vehtype || '',
        transdocdate: transdocdate,
        transdocno: editedInvoice.transdocno || '',
        tax_slabs: taxSlabs.map((slab, index) => ({
          slab_number: index + 1,
          tax_rate: slab.tax_rate,
          tax_amount: slab.tax_amount
        }))
      };
      await axiosInstance.put(`/invoices/${editedInvoice.id}/`, invoicePayload);
      const itemsToDelete = editedInvoiceItems.filter(item => item.id && item.markedForDeletion);
      for (const item of itemsToDelete) {
        await axiosInstance.delete(`/invoices/items/${item.id}/`);
      }
      for (const item of validItems) {
        const itemPayload = {
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          discount_percentage: parseFloat(item.discount_percentage) || 0,
          tax: parseFloat(item.tax) || 0,
          total_price: parseFloat((item.total_price || 0).toFixed(2)),
          discounted_price: parseFloat((item.discounted_price || 0).toFixed(2))
        };
        if (item.id) {
          await axiosInstance.put(`/invoices/items/${item.id}/`, itemPayload);
        } else {
          await axiosInstance.post(`/invoices/${editedInvoice.id}/items/`, itemPayload);
        }
      }
      // --- Log invoice update ---
      await frontendLogger.logUpdate('Invoice', {
        id: editedInvoice.id,
        action: 'update',
        updated_by: user?.id,
        user: user.username,
        updated_at: new Date().toISOString(),
      });
      // Refresh invoice detail after save
      await fetchInvoiceDetail();
      setOpenEditInvoiceModal(false);
      setSnackbar({
        open: true,
        message: 'Invoice saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving invoice:', error);
      const detail = error?.response?.data?.detail;
      let message = 'Failed to save invoice';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        message = detail[0].msg;
      }
      setSnackbar({
        open: true,
        message,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Download PDF handler (reuse from Invoices.js)
  const handleDownloadInvoice = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      // Fetch the templates to check the default template
      const templatesResponse = await axiosInstance.get('/templates/');
      const defaultTemplate = templatesResponse.data.find(template => template.is_default && template.type === 'invoice');
      const isIndiaTemplate = defaultTemplate && defaultTemplate.name === 'Standard India Template';
      const response = await axiosInstance.get(`/invoices/pdf/${id}`, {
        responseType: 'blob',
        params: { isIndiaTemplate },
        timeout: 30000
      });
      if (response.data.type !== 'application/pdf') {
        throw new Error('Invalid response format');
      }
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      let filename = `Invoice_${invoice?.invoice_number || id}.pdf`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename=(.+)/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          filename = fileNameMatch[1].replace(/["']/g, '');
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);
      // --- Log PDF download ---
      await frontendLogger.logCreate('Invoice', {
        // id: invoiceId,
        action: 'pdf_download',
        user: user?.id,
        user: user.username,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error downloading invoice:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Email modal handlers (adapted from Invoices.js)
  const handleToggleEmailModal = async () => {
    if (!isEmailModalOpen) {
      setIsEmailLoading(true);
      try {
        const emailConfigResponse = await axiosInstance.get('/email-settings');
        if (!emailConfigResponse.data?.email_provider) {
          throw new Error('Email is not configured. Please configure email settings first.');
        }
        if (!invoice || !invoice.account_id) {
          throw new Error('Invoice data is incomplete');
        }
        // Get account data
        const accountResponse = await axiosInstance.get(`/accounts/${invoice.account_id}`);
        const accountEmail = accountResponse.data.email || '';
        // Generate PDF blob
        const pdfResponse = await axiosInstance.get(`/invoices/pdf/${invoice.id}`, {
          responseType: 'blob'
        });
        const pdfBlob = new Blob([pdfResponse.data], { type: 'application/pdf' });
        // Check file size
        const maxSize = 25 * 1024 * 1024; // 25MB
        if (pdfBlob.size > maxSize) {
          throw new Error('PDF file exceeds 25MB limit');
        }
        setEmailAttachments([{
          filename: `Invoice_${invoice.invoice_number || invoice.id}.pdf`,
          size: pdfBlob.size,
          content_type: 'application/pdf',
          blob: pdfBlob,
        }]);
        setNewEmail({
          to: accountEmail,
          cc: '',
          bcc: '',
          subject: `Invoice ${invoice.invoice_number}`,
          content: `Please find attached Invoice ${invoice.invoice_number}`,
        });
      } catch (error) {
        const message =
          error?.message?.includes('not configured')
            ? error.message
            : error?.response?.status === 404
              ? 'Email is not configured. Please configure email settings first.'
              : `Error preparing email: ${error.message}`;
        setSnackbar({
          open: true,
          message,
          severity: 'error'
        });
        return;
      } finally {
        setIsEmailLoading(false);
      }
    } else {
      setEmailAttachments([]);
    }
    setIsEmailModalOpen(!isEmailModalOpen);
  };

  const handleSendEmail = async () => {
    setIsEmailSending(true);
    try {
      // Check if email service is configured
      const emailConfigResponse = await axiosInstance.get('/email-settings');
      if (!emailConfigResponse.data?.email_provider) {
        throw new Error('Email is not configured. Please configure email settings first.');
      }

      const attachments = await serializeEmailAttachments(emailAttachments);

      const emailPayload = {
        to: newEmail.to,
        cc: newEmail.cc || '',
        bcc: newEmail.bcc || '',
        subject: newEmail.subject,
        content: newEmail.content,
        attachments: attachments.length > 0 ? attachments : undefined,
        metadata: {
          parent_type: 'invoice',
          parent_id: invoice.id
        }
      };
      await axiosInstance.post('/emails/send', emailPayload);
      setIsEmailModalOpen(false);
      setEmailAttachments([]);
      setSnackbar({
        open: true,
        message: 'Email sent successfully!',
        severity: 'success'
      });
    } catch (error) {
      let errorMessage = 'Failed to send email';
      if (error.message.includes('not configured')) {
        errorMessage = error.message;
      } else if (error.response?.status === 404) {
        errorMessage = 'Email is not configured. Please configure email settings first.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsEmailSending(false);
    }
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };
  // Add this useEffect hook after other useEffects
  useEffect(() => {
    const handlePdfAttachment = async () => {
      try {
        // Get current attachments
        const response = await axiosInstance.get('/attachments/', {
          params: {
            parent_table: 'invoices',
            parent_id: id,
          }
        });

        const currentAttachments = response.data || [];
        setAttachments(currentAttachments);

        // Check if GST invoice exists and has IRN
        const gstInvoice = await axiosInstance.get(`/gst-invoices/${id}`);
        const hasIrn = gstInvoice?.data?.irn;
        const hasPdf = currentAttachments.some(attachment =>
          attachment.file_name?.toLowerCase().includes('gst') &&
          attachment.file_type === 'application/pdf'
        );

        // Only generate PDF if:
        // 1. GST invoice has IRN
        // 2. No PDF attachment exists
        // 3. This is the first time IRN is generated
        const shouldGeneratePdf = hasIrn && !hasPdf && hasIrn !== previousGstStatus.current;

        if (shouldGeneratePdf) {
          const pdfBlob = await downloadInvoice(invoice.id, false);
          if (pdfBlob) {
            // Upload to storage
            const uploadResponse = await uploadDocument(pdfBlob);

            // Create attachment record
            const attachmentPayload = {
              file_name: `Invoice_${invoice.invoice_number}_GST.pdf`,
              file_type: 'application/pdf',
              content_type: 'application/pdf',
              bucket_name: uploadResponse.bucket,
              description: `GST E-Invoice PDF with IRN: ${hasIrn}`,
              parent_table: 'invoices',
              parent_id: id,
              is_active: true,
              is_private: false,
              file_size: pdfBlob.size,
              file_url: uploadResponse.url,
              storage_path: uploadResponse.path,
            };

            await axiosInstance.post('/attachments/', attachmentPayload);

            // Refresh attachments list
            const updatedResponse = await axiosInstance.get('/attachments/', {
              params: {
                parent_table: 'invoices',
                parent_id: id,
              }
            });
            setAttachments(updatedResponse.data || []);
          }
        }

        // Update previous status
        previousGstStatus.current = hasIrn;

      } catch (error) {
        console.error('Error managing attachments:', error);
        enqueueSnackbar(
          error.response?.data?.detail || 'Error managing attachments',
          { variant: 'error' }
        );
      }
    };

    if (invoice?.id) {
      handlePdfAttachment();
    }
  }, [id, invoice?.id, invoice?.invoice_number]); // Dependencies

  // Modify the fetchAttachments function
  const fetchAttachments = async () => {
    try {
      const response = await axiosInstance.get('/attachments/', {
        params: {
          parent_table: 'invoices',
          parent_id: id,
          sort_by: 'created_at',
          sort_order: 'desc'
        }
      });

      setAttachments(response.data || []);

    } catch (error) {
      console.error('Error fetching attachments:', error);
      enqueueSnackbar(
        error.response?.data?.detail || 'Error fetching attachments',
        { variant: 'error' }
      );
    }
  };

  // Add attachment handlers
  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axiosInstance.delete(`/attachments/${attachmentId}`);
      setAttachments(attachments.filter((attachment) => attachment.id !== attachmentId));
      setSnackbar({
        open: true,
        message: 'Attachment deleted successfully!',
        severity: 'success',
      });
      // --- Log attachment delete ---
      await frontendLogger.logDelete('Attachment', {
        id: attachmentId,
        parent_type: 'Invoice',
        parent_id: invoice?.id,
        action: 'delete',
        deleted_by: user?.id,
        user: user.username,
        deleted_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting attachment',
        severity: 'error',
      });
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (!attachment.bucket_name || !attachment.storage_path) {
        throw new Error('Attachment storage metadata is invalid for strict R2 download');
      }
      await downloadPrivateObject({
        axiosInstance,
        attachmentId: attachment.id,
        fileName: attachment.file_name
      });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error downloading attachment',
        severity: 'error',
      });
    }
  };

  const handleUploadSuccess = async (uploadData) => {
    try {
      const uploadDataPayload = {
        file_name: uploadData.file.name,
        file_type: uploadData.file.type,
        file_size: uploadData.file.size,
        bucket_name: uploadData.bucket,
        storage_path: uploadData.path,
        description: uploadData.description || '',
        parent_table: 'invoices',
        parent_id: id,
        is_active: true,
        is_private: true,
        file_url: uploadData.url || null,
        signed_url_expiry: uploadData.signedUrlExpiry || null,
      };
      setIsAttachmentModalOpen(false);
      const response = await axiosInstance.post(`/attachments/`, uploadDataPayload);
      setSnackbar({
        open: true,
        message: 'File uploaded successfully!',
        severity: 'success',
      });
      fetchAttachments();
      // --- Log attachment upload ---
      await frontendLogger.logCreate('Attachment', {
        id: response.data.id,
        parent_type: 'Invoice',
        parent_id: response.data.parent_id,
        action: 'upload',
        uploaded_by: user?.id,
        user: user.username,
        uploaded_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      setSnackbar({
        open: true,
        message: 'Error uploading file',
        severity: 'error',
      });
    }
  };

  // Prevent generating GST e-invoices
  const handleGenerateGSTEinvoice = async () => {
    // Clear any previous errors
    setError('');
    setSnackbar({ open: false, message: '', severity: 'info' });

    // Prevent support users from generating GST e-invoices
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to generate GST e-invoices.',
        severity: 'error',
      });
      return;
    }

    // Validate transport details if e-way bill is checked
    if (isEwayBillChecked) {
      let validationError = '';

      switch (invoice.transmode) {
        case '1': // Road
          if (!invoice.vehno || !invoice.vehtype) {
            validationError = 'For Road transport, Vehicle Number and Type are required for e-way bill';
          } else if (!/^[A-Z]{2}[0-9]{4,6}$/.test(invoice.vehno)) {
            validationError = 'Vehicle Number must be in format like KA123456 (2 letters followed by 4-6 digits)';
          }
          invoice.transdocno = '';
          invoice.transdocdate = '';
          break;
        case '2': // Rail
          if (!invoice.transdocno || !invoice.transdocdate) {
            validationError = 'For Rail transport, Transport Document Number and Date are required for e-way bill';
          }
          break;
        case '3': // Air
          if (!invoice.transdocno || !invoice.transdocdate) {
            validationError = 'For Air transport, Transport Document Number and Date are required for e-way bill';
          }
          break;
        case '4': // Ship
          if (
            (!invoice.vehno || !invoice.vehtype) &&
            (!invoice.transdocno || !invoice.transdocdate)
          ) {
            validationError = 'For Ship, either Vehicle Number and Type or Transport Document Number and Date are required';
          } else if (invoice.vehno && !/^[A-Z]{2}[0-9]{4,6}$/.test(invoice.vehno)) {
            validationError = 'Vehicle Number must be in format like KA123456 (2 letters followed by 4-6 digits)';
          }
          break;
        default:
          validationError = 'Please select a valid transport mode';
      }

      if (validationError) {
        setSnackbar({
          open: true,
          message: validationError,
          severity: 'error',
          autoHideDuration: 10000
        });
        return;
      }
    }

    try {
      setIsGstDialogGenerating(true);
      setIsGstGenerating(true);

      const endpoint = isEwayBillChecked
        ? `/invoices/${invoice.id}/gst-einvoice-with-eway/`
        : `/invoices/${invoice.id}/gst-einvoice/`;

      // Log the invoice and transport details being sent
      // console.log("Sending GST E-invoice/E-way bill request:");
      // console.log("Endpoint:", endpoint);
      // console.log("Invoice Data:", invoice);
      // console.log("Transport Details:", {
      //   transid: invoice.transid,
      //   transname: invoice.transname,
      //   transmode: invoice.transmode,
      //   vehno: invoice.vehno,
      //   vehtype: invoice.vehtype,
      //   transdocno: invoice.transdocno,
      //   transdocdate: invoice.transdocdate,
      // });

      const response = await axiosInstance.post(endpoint, null, {
        timeout: 60000 // 60 seconds timeout
      });

      // Log the response from the API
      // console.log("GST E-invoice/E-way bill API response:", response.data);

      // Handle response
      if (response.data.status === 0) {
        throw new Error(response.data.message || 'Failed to generate GST E-Invoice');
      }

      // Successful generation
      const irn = response.data.irn || response.data.Irn;
      const ewbNo = response.data.ewbNo || response.data.EwbNo;
      const ackNo = response.data.ackNo || response.data.AckNo;
      const ackDate = response.data.ackDt || response.data.AckDt;

      // Log the response data
      console.log('GST E-Invoice Generation Response:', {
        irn,
        ewbNo,
        ackNo,
        ackDate,
        fullResponse: response.data
      });

      let successMessage = response.data.message ||
        (isEwayBillChecked
          ? 'GST E-Invoice with E-way Bill generated successfully!'
          : 'GST E-Invoice generated successfully!');

      // Add details to success message
      const details = [];
      if (irn) details.push(`IRN: ${irn}`);
      if (ackNo) details.push(`Ack. No: ${ackNo}`);
      if (ackDate) details.push(`Ack. Date: ${formatDate(ackDate)}`);
      if (ewbNo) details.push(`E-Way Bill No: ${ewbNo}`);

      if (details.length > 0) {
        successMessage += `\n\n${details.join('\n')}`;
      }

      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success',
        autoHideDuration: 15000 // Show longer for important info
      });

      // Update invoice flags
      try {
        console.log('Updating invoice flags:', {
          invoiceId: invoice.id,
          is_gst_invoice_generated: !!irn,
          is_eway_bill_generated: !!ewbNo,
          irn,
          ewbNo
        });

        // Create update payload with only the necessary fields
        const updatePayload = {
          id: invoice.id,
          invoice_number: invoice.invoice_number,
          invoice_date: invoice.invoice_date,
          due_date: invoice.due_date,
          status: invoice.status,
          notes: invoice.notes || '',
          total_amount: invoice.total_amount,
          subtotal: invoice.subtotal,
          account_id: invoice.account_id,
          account_name: invoice.account_name,
          currency: invoice.currency,
          is_gst_invoice_generated: !!irn,
          is_eway_bill_generated: !!ewbNo,
          irn: irn || null,
          eway_bill_no: ewbNo || null
        };

        // Only add transport details if they exist
        if (invoice.transid) updatePayload.transid = invoice.transid;
        if (invoice.transname) updatePayload.transname = invoice.transname;
        if (invoice.transmode) updatePayload.transmode = invoice.transmode;
        if (invoice.vehno) updatePayload.vehno = invoice.vehno;
        if (invoice.vehtype) updatePayload.vehtype = invoice.vehtype;
        if (invoice.transdocno) updatePayload.transdocno = invoice.transdocno;
        if (invoice.transdocdate) updatePayload.transdocdate = invoice.transdocdate;

        console.log('Sending update payload:', updatePayload);

        const updateResponse = await axiosInstance.put(`/invoices/${invoice.id}/`, updatePayload);

        console.log('Invoice update response:', updateResponse.data);

        // Update local state to reflect changes
        setInvoice(prevInvoice => ({
          ...prevInvoice,
          is_gst_invoice_generated: !!irn,
          is_eway_bill_generated: !!ewbNo,
          irn: irn || null,
          eway_bill_no: ewbNo || null
        }));

      } catch (updateError) {
        console.error('Error updating invoice flags:', updateError);
        if (updateError.response?.data?.detail) {
          console.error('Validation errors:', updateError.response.data.detail);
        }
        enqueueSnackbar('E-invoice generated but failed to update invoice status', {
          variant: 'warning',
          autoHideDuration: 10000
        });
      }

      // Generate and attach PDF
      try {
        const pdfBlob = await downloadInvoice(invoice.id, false);
        if (pdfBlob) {
          const uploadResponse = await uploadDocument(pdfBlob);

          const attachmentPayload = {
            file_name: `Invoice_${invoice.invoice_number}_GST.pdf`,
            file_type: 'application/pdf',
            content_type: 'application/pdf',
            bucket_name: uploadResponse.bucket,
            description: `GST E-Invoice${irn ? ` with IRN: ${irn}` : ''}`,
            parent_table: 'invoices',
            parent_id: invoice.id,
            is_active: true,
            is_private: false,
            file_size: pdfBlob.size,
            file_url: uploadResponse.url,
            storage_path: uploadResponse.path,
          };

          await axiosInstance.post('/attachments/', attachmentPayload);
          await fetchAttachments();

          // Trigger download for user
          const blobUrl = window.URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.setAttribute('download', `Invoice_${invoice.invoice_number}_GST.pdf`);
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(link);
        }
      } catch (pdfError) {
        console.error('Error in PDF processing:', pdfError);
        enqueueSnackbar('E-invoice generated but failed to process PDF', {
          variant: 'warning',
          autoHideDuration: 10000
        });
      }

      // Close dialog and refresh data
      setIsGstDialogOpen(false);
      setIsEwayBillChecked(false);
      await fetchInvoiceDetail();
      setHasIRN(!!irn);

    } catch (error) {
      console.error('Error in GST E-Invoice generation:', error);

      let errorMessage = 'Failed to generate GST E-Invoice';
      if (error.response) {
        // Handle API response errors
        if (error.response.data) {
          if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data)) {
            errorMessage = error.response.data.map(err =>
              err.msg || err.message || JSON.stringify(err)
            ).join('\n');
          } else if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          }
        } else {
          errorMessage = `Server responded with status ${error.response.status}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
        autoHideDuration: 15000 // Show longer for errors
      });

    } finally {
      setIsGstDialogGenerating(false);
      setIsGstGenerating(false);
    }
    // --- Log GST generation ---
    await frontendLogger.logUpdate('Invoice', {
      id: invoice.id,
      action: 'gst_generate',
      user: user?.id,
      user: user.username,
      timestamp: new Date().toISOString(),
    });
  };

  // Prevent canceling invoices
  const handleCancelGSTEinvoice = async (invoiceId) => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to cancel invoices.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    try {
      const response = await axiosInstance.post(`/invoices/${invoiceId}/gst-einvoice/cancel`);
      console.log("GST E-Invoice cancel response:", response.data);
      setHasIRN(false);  // Set hasIRN to false when cancelled
      enqueueSnackbar('Successfully cancelled GST E-Invoice!', { variant: 'success' });
      await fetchInvoiceDetail();
      // --- Log GST cancel ---
      await frontendLogger.logUpdate('Invoice', {
        id: invoiceId,
        action: 'gst_cancel',
        user: user?.id,
        user: user.username,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      let errorMessage = 'Failed to cancel GST E-Invoice';

      if (error.response?.data) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (typeof error.response.data.message === 'string') {
          errorMessage = error.response.data.message;
        } else if (Array.isArray(error.response.data)) {
          errorMessage = error.response.data.map(err => err.msg).join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Check if invoice is cancelled
  const isInvoiceCancelled = invoice?.status === 'Cancelled';

  const handleQuillChange = (content) => {
    setNewEmail({ ...newEmail, content: content });
  };
  const toggleCc = () => setShowCc(!showCc);
  const toggleBcc = () => setShowBcc(!showBcc);

  // Inline editing functions for unit prices
  const handleStartPriceEdit = (index, currentPrice) => {
    setEditingPriceIndex(index);
    setEditingPriceValue(currentPrice.toString());
  };

  const handleCancelPriceEdit = () => {
    setEditingPriceIndex(null);
    setEditingPriceValue('');
  };

  const handleSavePriceEdit = async (index) => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to edit invoices.',
        severity: 'error',
      });
      return;
    }

    setIsUpdatingPrice(true);
    try {
      const item = invoiceItems[index];
      const newPrice = parseFloat(editingPriceValue);
      
      if (isNaN(newPrice) || newPrice < 0) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid price',
          severity: 'error',
        });
        return;
      }

      // Calculate new values
      const discountAmount = newPrice * (item.discount_percentage / 100);
      const discountedPrice = newPrice - discountAmount;
      const totalPrice = discountedPrice * item.quantity;

      // Update the invoice item
      await axiosInstance.put(`/invoices/items/${item.id}/`, {
        ...item,
        unit_price: newPrice,
        discounted_price: discountedPrice,
        total_price: totalPrice,
      });

      // Update local state
      const updatedInvoiceItems = [...invoiceItems];
      updatedInvoiceItems[index] = {
        ...updatedInvoiceItems[index],
        unit_price: newPrice,
        discounted_price: discountedPrice,
        total_price: totalPrice,
      };

      // Recalculate invoice total
      const newTotalAmount = updatedInvoiceItems.reduce((total, item) => total + parseFloat(item.total_price), 0);

      setInvoiceItems(updatedInvoiceItems);
      setInvoice(prevInvoice => ({
        ...prevInvoice,
        total_amount: newTotalAmount,
      }));

      setSnackbar({
        open: true,
        message: 'Unit price updated successfully!',
        severity: 'success',
      });

      setEditingPriceIndex(null);
      setEditingPriceValue('');
    } catch (error) {
      console.error('Error updating unit price:', error);
      setSnackbar({
        open: true,
        message: 'Error updating unit price',
        severity: 'error',
      });
    } finally {
      setIsUpdatingPrice(false);
    }
  };

  // Helper to render clickable fields
  const renderFieldValue = (key, value) => {
    if (key === 'Account' && invoice.account_id) {
      return (
        <MDTypography
          fontSize="0.9rem"
          color="info"
          fontWeight="medium"
          ml={1}
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/accounts/${invoice.account_id}`)}
        >
          {value}
        </MDTypography>
      );
    }
    if (key === 'Order Number' && invoice.sales_order_id) {
      return (
        <MDTypography
          fontSize="0.9rem"
          color="info"
          fontWeight="medium"
          ml={1}
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/orders/${invoice.sales_order_id}`)}
        >
          {invoice.order_number || `Order #${invoice.sales_order_id}`}
        </MDTypography>
      );
    }
    if ((key.toLowerCase().includes('amount') || key.toLowerCase().includes('total') || key.toLowerCase().includes('subtotal') || key.startsWith('Tax (')) && value !== undefined) {
      return (
        <MDTypography fontSize="0.9rem" color="text" ml={1}>
          {formatCurrency(parseFloat(value) || 0, invoice?.currency)}
        </MDTypography>
      );
    }
    return (
      <MDTypography fontSize="0.9rem" color="text" ml={1}>{value}</MDTypography>
    );
  };

  // Modify the renderDetailCard function to handle clickable account names
  const renderDetailCard = useCallback((title, data) => (
    <MDBox m={3} p={3} borderRadius="lg" sx={{ backgroundColor: 'white', boxShadow: 3 }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6" fontWeight="medium" color="dark">
          {title}
        </MDTypography>
      </MDBox>
      <Grid container spacing={3}>
        {Object.entries(data).map(([key, value]) => (
          <Grid item xs={12} sm={6} key={key}>
            <MDBox display="flex" alignItems="center">
              <MDTypography variant="button" fontWeight="bold" color="text">
                {key}:
              </MDTypography>
              {(() => {
                // Handle clickable account names
                if (key === 'Account' && invoice.account_id) {
                  return (
                    <MDTypography
                      component="span"
                      onClick={() => navigate(`/accounts/${invoice.account_id}`)}
                      variant="button"
                      color="info"
                      fontWeight="medium"
                      ml={1}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {capitalizeFirstLetter(invoice.account_name || ' ')}
                    </MDTypography>
                  );
                }

                // Handle clickable order numbers
                if (key === 'Order Number' && (invoice.sales_order_id || invoice.travel_order_id)) {
                  return (
                    <MDTypography
                      component="span"
                      onClick={() => {
                        if (invoice.travel_order_id) {
                          navigate(`/travel-order/${invoice.travel_order_id}`);
                          return;
                        }
                        if (invoice.sales_order_id) {
                          navigate(`/orders/${invoice.sales_order_id}`);
                        }
                      }}
                      variant="button"
                      color="info"
                      fontWeight="medium"
                      ml={1}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {invoice.order_number || (invoice.travel_order_id ? `Travel Order #${invoice.travel_order_id}` : `Order #${invoice.sales_order_id}`)}
                    </MDTypography>
                  );
                }

                // Handle currency fields
                if ((key.toLowerCase().includes('amount') || key.toLowerCase().includes('total') || key.toLowerCase().includes('subtotal') || key.startsWith('Tax (')) && value !== undefined) {
                  return (
                    <MDTypography fontSize="0.9rem" color="text" ml={1}>
                      {formatCurrency(parseFloat(value) || 0, invoice?.currency)}
                    </MDTypography>
                  );
                }

                // Capitalize first letter for string values, but skip email and currency fields
                if (typeof value === 'string' && value && !key.toLowerCase().includes('email') && !key.toLowerCase().includes('currency')) {
                  return (
                    <MDTypography fontSize="0.9rem" color="text" ml={1}>
                      {capitalizeFirstLetter(value)}
                    </MDTypography>
                  );
                }

                // For other values (numbers, dates, etc.), return as is
                return (
                  <MDTypography fontSize="0.9rem" color="text" ml={1}>
                    {value}
                  </MDTypography>
                );
              })()}
            </MDBox>
          </Grid>
        ))}
      </Grid>
    </MDBox>
  ), [formatCurrency, invoice?.currency, invoice?.account_id, invoice?.sales_order_id, invoice?.travel_order_id, invoice?.order_number, navigate]);

  // Modify the renderAuditFields function
  const renderAuditFields = () => (
    <Grid container spacing={3}>
      {auditFields.map((item) => (
        <Grid item xs={12} md={6} key={item.field}>
          <MDBox display="flex" alignItems="center">
            <MDTypography variant="button" fontWeight="bold" color="text">
              {item.label}:
            </MDTypography>
            <MDTypography fontSize="0.9rem" color="text" ml={1}>
              {(() => {
                const value = item.field === 'created_at' || item.field === 'updated_at'
                  ? formatDate(invoice[item.field]) // Format the date using formatDate
                  : invoice[item.field] || ' ';

                // Capitalize first letter for string values, but skip email and date fields
                if (typeof value === 'string' && value &&
                  !item.field?.includes('email') &&
                  item.field !== 'created_at' &&
                  item.field !== 'updated_at') {
                  return capitalizeFirstLetter(value);
                }
                return value;
              })()}
            </MDTypography>
          </MDBox>
        </Grid>
      ))}
    </Grid>
  );


  const renderInvoiceItemsTable = useCallback(() => {
    if (!invoiceItems.length) return null;

    return (
      <MDBox
        m={3}
        p={3}
        borderRadius="lg"
        sx={{
          backgroundColor: 'white',
          boxShadow: 3,
          maxWidth: '100%',
          width: 'auto'
        }}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <MDTypography variant="h6" fontWeight="medium" color="dark">
            Invoice Items
          </MDTypography>
        </MDBox>

        <MDBox
          sx={{
            mt: 3,
            mb: 3,
            border: "1px solid #dee2e6",
            borderRadius: "0.75rem",
            overflow: "auto",
            maxHeight: "400px",
          }}
        >
          {(() => {
            const itemsGridSx = {
              display: "grid",
              gridTemplateColumns:
                "minmax(180px, 2.2fr) minmax(110px, 1fr) minmax(72px, 0.7fr) minmax(100px, 0.85fr) minmax(130px, 1.1fr) minmax(90px, 0.75fr) minmax(130px, 1.1fr)",
              alignItems: "center",
              width: "100%",
              minWidth: 920,
            };
            const headerCellSx = {
              px: 1.5,
              py: 1.5,
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              lineHeight: 1.35,
            };
            // PSA-373: line-item cells wrap when the column is narrow.
            const valueCellSx = {
              px: 1.5,
              py: 1.5,
              textAlign: "right",
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              lineHeight: 1.35,
            };

            const stage = String(invoice?.payment_stage || "").trim().toLowerCase();
            const kind = String(invoice?.invoice_type || "").trim().toLowerCase();
            const isFullStage = stage === "full payment" || stage === "full" || (kind === "full" && !stage);
            const isPartialStage = !isFullStage && (
              stage === "deposit" || stage === "final balance" || stage.startsWith("installment")
              || kind === "deposit" || kind === "partial" || kind === "installment" || kind === "final"
            );
            const orderTotal = parseFloat(invoice?.total_order_amount) || 0;
            const paid = parseFloat(invoice?.total_amount) || 0;
            let lineShare = 1;
            if (invoice?.travel_order_id && isPartialStage && orderTotal > 0 && paid > 0 && paid < orderTotal) {
              const onlyStub = invoiceItems.length === 1 && (
                !String(invoiceItems[0].product_name || "").trim()
                || ["full payment", "deposit", "final balance", "installment", "partial"].includes(
                  String(invoiceItems[0].product_name || "").trim().toLowerCase()
                )
                || String(invoiceItems[0].product_name || "").trim().toLowerCase().startsWith("installment ")
              );
              if (!onlyStub) {
                let sub = 0;
                let tax = 0;
                invoiceItems.forEach((item) => {
                  const qty = parseFloat(item.quantity) || 0;
                  const unit = parseFloat(item.unit_price) || 0;
                  const disc = parseFloat(item.discount_percentage) || 0;
                  const rate = parseFloat(item.tax) || 0;
                  const net = qty * unit * (1 - disc / 100);
                  sub += net;
                  if (rate > 0) tax += net * (rate / 100);
                });
                const gross = sub + tax;
                const alreadyThisInvoice = Math.abs(gross - paid) <= Math.max(0.05, paid * 0.01);
                const nearOrder = Math.abs(gross - orderTotal) <= Math.max(0.5, orderTotal * 0.12);
                const exceedsPayment = sub > paid * 1.01;
                if (!alreadyThisInvoice && (nearOrder || exceedsPayment)) {
                  lineShare = paid / orderTotal;
                }
              }
            }

            return (
              <>
                {/* Header */}
                <MDBox
                  sx={{
                    ...itemsGridSx,
                    bgcolor: "#f8f9fa",
                    borderBottom: "1px solid #dee2e6",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <MDBox sx={{ ...headerCellSx, textAlign: "left" }}>
                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                      Product Name
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                      Unit Price
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                      Tax %
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                      Discount %
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                      Discounted Price
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                      Quantity
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                    <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                      Total Net Price
                    </MDTypography>
                  </MDBox>
                </MDBox>

                {/* Body */}
                {invoiceItems.map((item, index) => {
                  const unitPrice = (parseFloat(item.unit_price) || 0) * lineShare;
                  const discountedPrice = unitPrice - (unitPrice * ((parseFloat(item.discount_percentage) || 0) / 100));
                  const totalNetPrice = discountedPrice * (parseFloat(item.quantity) || 0);
                  return (
                    <MDBox
                      key={index}
                      sx={{
                        ...itemsGridSx,
                        borderBottom: "1px solid #dee2e6",
                        "&:hover": { backgroundColor: "#f8f9fa" },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <MDBox sx={{ ...headerCellSx, textAlign: "left" }}>
                        <MDTypography
                          variant="button"
                          color="text"
                          sx={{
                            display: "block",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: 1.35,
                          }}
                          title={item.product_name}
                        >
                          {item.product_name}
                        </MDTypography>
                      </MDBox>
                      <MDBox sx={valueCellSx}>
                        <MDTypography variant="button" color="text">
                          {formatCurrency(unitPrice, invoice.currency)}
                        </MDTypography>
                      </MDBox>
                      <MDBox sx={valueCellSx}>
                        <MDTypography variant="button" color="text">
                          {item.tax}%
                        </MDTypography>
                      </MDBox>
                      <MDBox sx={valueCellSx}>
                        <MDTypography variant="button" color="text">
                          {item.discount_percentage}%
                        </MDTypography>
                      </MDBox>
                      <MDBox sx={valueCellSx}>
                        <MDTypography variant="button" color="text">
                          {formatCurrency(parseFloat(discountedPrice), invoice.currency)}
                        </MDTypography>
                      </MDBox>
                      <MDBox sx={valueCellSx}>
                        <MDTypography variant="button" color="text">
                          {item.quantity}
                        </MDTypography>
                      </MDBox>
                      <MDBox sx={valueCellSx}>
                        <MDTypography variant="button" color="text">
                          {formatCurrency(parseFloat(totalNetPrice), invoice.currency)}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  );
                })}

                {/* Footer */}
                <MDBox
                  sx={{
                    ...itemsGridSx,
                    borderTop: "2px solid #dee2e6",
                    backgroundColor: "white",
                  }}
                >
                  <MDBox sx={{ ...valueCellSx, gridColumn: "1 / 7" }}>
                    <MDTypography variant="button" fontWeight="medium" color="dark">
                      Total
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={valueCellSx}>
                    <MDTypography variant="button" fontWeight="medium" color="dark">
                      {formatCurrency(parseFloat(invoice.total_amount), invoice.currency)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </>
            );
          })()}
        </MDBox>
      </MDBox>
    );
  }, [invoiceItems, formatCurrency, invoice]);

  // Add renderCustomFields function
  const renderCustomFields = () => {
    if (!customFields || customFields.length === 0) {
      return null;
    }
    return (
      <CustomFieldsSection
        ref={customFieldsRef}
        objectType="invoices"
        objectData={invoice}
        isEditing={false}
        onFieldUpdate={handleFieldUpdate}
        onError={handleFieldError}
        formErrors={formErrors}
      />
    );
  };

  // Modify handleGenerateGSTEinvoice function to use the automatic attachment process
  const renderGSTDialog = () => (
    <Dialog
      open={isGstDialogOpen}
      onClose={() => !isGstDialogGenerating && setIsGstDialogOpen(false)}
      aria-labelledby="gst-dialog-title"
      aria-describedby="gst-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{
        p: 3,
        backgroundColor: colors.background.default
      }}>
        <MDTypography variant="h6" component="h2" gutterBottom>
          Generate GST E-Invoice
        </MDTypography>

        {isEwayBillChecked && (
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text.secondary">
              {invoice.transmode === '1' && "For Road transport, please ensure Vehicle Number and Type are filled."}
              {invoice.transmode === '2' && "For Rail transport, please ensure Transport Document Number and Date are filled."}
              {invoice.transmode === '3' && "For Air transport, please ensure Transport Document Number and Date are filled."}
              {invoice.transmode === '4' && "For Ship transport, either vehicle number or transport document number is required."}
            </MDTypography>
          </MDBox>
        )}

        <MDTypography variant="body2" color="text.secondary" mb={2}>
          Are you sure you want to generate the GST E-Invoice? Once generated, you won't be able to edit the invoice.
        </MDTypography>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isEwayBillChecked}
                onChange={(e) => setIsEwayBillChecked(e.target.checked)}
                color="primary"
                disabled={isGstDialogGenerating}
              />
            }
            label="Generate E-way Bill along with GST E-Invoice"
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <MDButton
            onClick={() => setIsGstDialogOpen(false)}
            variant="outlined"
            color="secondary"
            disabled={isGstDialogGenerating}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleGenerateGSTEinvoice}
            variant="gradient"
            color="info"
            disabled={isGstDialogGenerating}
            startIcon={isGstDialogGenerating && <CircularProgress size={20} color="inherit" />}
          >
            {isGstDialogGenerating ? 'Generating...' : 'Generate'}
          </MDButton>
        </Box>
      </Box>
    </Dialog>
  );

  // Add Attachments Card in the Grid layout
  const renderAttachmentsCard = () => (
    <Card>
      <MDBox
        mx={2}
        mt={-3}
        py={2}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AttachmentIcon sx={{ color: 'white !important', mr: 1 }} />
          <MDTypography variant="h6" color="white">
            Generated Docs
          </MDTypography>
        </Box>
        <MDButton variant="gradient" color="dark" onClick={() => setIsAttachmentModalOpen(true)}>
          Add Attachment
        </MDButton>
      </MDBox>
      <MDBox p={2}>
        <Attachments
          attachments={attachments}
          onDelete={handleDeleteAttachment}
          onDownload={handleDownloadAttachment}
        />
      </MDBox>
    </Card>
  );

  // Add the cancel confirmation dialog render function
  const renderCancelDialog = () => (
    <Dialog
      open={isCancelDialogOpen}
      onClose={() => !isCancelling && setIsCancelDialogOpen(false)}
      aria-labelledby="cancel-dialog-title"
      aria-describedby="cancel-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{
        p: 3,
        // mb: 2,
        backgroundColor: colors.background.default
      }}>
        <MDTypography variant="h6" component="h2" gutterBottom>
          Generate GST E-Invoice
        </MDTypography>

        <MDTypography variant="body2" color="text.secondary" mb={2}>
          Are you sure you want to cancel this GST E-Invoice? This action cannot be undone.
        </MDTypography>

        <MDTypography variant="body2" color="error" mb={3} pl={1}>
          Warning: Once cancelled, you will not be able to:
          <ul>
            <li>Edit the invoice</li>
            <li>Generate PDF</li>
            <li>Generate GST E-Invoice and E-way bill</li>
          </ul>
        </MDTypography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <MDButton
            onClick={() => setIsCancelDialogOpen(false)}
            variant="outlined"
            color="secondary"
            disabled={isCancelling}
          >
            No, Keep E-Invoice
          </MDButton>
          <MDButton
            onClick={async () => {
              setIsCancelling(true);
              try {
                await handleCancelGSTEinvoice(invoice.id);
                setIsCancelDialogOpen(false);
              } finally {
                setIsCancelling(false);
              }
            }}
            variant="gradient"
            color="info"
            disabled={isCancelling}
            startIcon={isCancelling && <CircularProgress size={20} color="inherit" />}
          >
            {isCancelling ? 'Cancelling...' : 'Yes, Cancel E-Invoice'}
          </MDButton>
        </Box>
      </Box>
    </Dialog>
  );

  // Now do early returns
  // Wait for travel UI before gating travel vs legacy invoice visibility.
  if (isLoading || (travelUiLoading && invoice)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <DataLoadingSpinner title="Loading Invoice Details" message="Please wait while we fetch the invoice information..." />
        
      </DashboardLayout>
    );
  }

  if (!isTravelEnabled && invoice?.travel_order_id) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6">Travel invoice feature is disabled.</MDTypography>
            </MDBox>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  // Travel mode hides legacy Orders; block sales-order invoices only. Project /
  // Real Estate invoices (no sales_order_id) must stay openable.
  if (isTravelEnabled && invoice?.sales_order_id && !invoice.travel_order_id) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6">
                Legacy order invoices are hidden while Travel is enabled.
              </MDTypography>
            </MDBox>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={6}><MDTypography color="error">{error}</MDTypography></MDBox>
        
      </DashboardLayout>
    );
  }

  if (!invoice) {
    return null;
  }

  const isPaidStatus = (status) => String(status || '').trim().toLowerCase() === 'paid';
  const currentScheduleRow = paymentSchedule.find(
    (row) => row.invoice_id === Number(id) || row.is_current
  );
  const hasPaymentGateway = Boolean(gatewayAvailability?.stripe || gatewayAvailability?.razorpay);
  const canRequestPayment =
    !isInvoiceCancelled &&
    (
      !isPaidStatus(invoice.payment_status) ||
      (currentScheduleRow && !isPaidStatus(currentScheduleRow.payment_status))
    );
  const paymentLinkDisabledReason = !hasPaymentGateway
    ? 'Connect Stripe in Settings → Payments first'
    : '';

  // Calculate subtotal from invoice items if not provided correctly by backend
  const calculateSubtotalFromItems = () => {
    if (!invoiceItems || invoiceItems.length === 0) return invoice.subtotal || 0;
    
    return invoiceItems.reduce((total, item) => {
      const unitPrice = parseFloat(item.unit_price) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const discountPercentage = parseFloat(item.discount_percentage) || 0;
      const discountedPrice = unitPrice - (unitPrice * (discountPercentage / 100));
      const itemSubtotal = discountedPrice * quantity;
      return total + itemSubtotal;
    }, 0);
  };

  const calculatedSubtotal = calculateSubtotalFromItems();
  const travelTaxLines = [1, 2, 3, 4, 5].reduce((rows, index) => {
    const rate = invoice[`tax_slab_${index}`];
    const amount = invoice[`tax_slab_amount_${index}`];
    const numericAmount = parseFloat(amount);
    if (rate && !Number.isNaN(numericAmount) && numericAmount > 0) {
      rows.push([`Tax (${rate}%)`, numericAmount]);
    }
    return rows;
  }, []);
  const travelTaxTotal = travelTaxLines.reduce((sum, [, amount]) => sum + amount, 0);
  const paidAmount = parseFloat(invoice.total_amount) || 0;
  const displaySubtotal = invoice.travel_order_id && travelTaxTotal > 0 && paidAmount > travelTaxTotal
    ? paidAmount - travelTaxTotal
    : (invoice.subtotal !== invoice.total_amount ? invoice.subtotal : calculatedSubtotal);

  // Debug logging
  console.log('[InvoiceDetail] Backend subtotal:', invoice.subtotal);
  console.log('[InvoiceDetail] Backend total_amount:', invoice.total_amount);
  console.log('[InvoiceDetail] Calculated subtotal from items:', calculatedSubtotal);
  console.log('[InvoiceDetail] Display subtotal:', displaySubtotal);

  // Now it's safe to use invoice properties!
  const isTravelInvoice = Boolean(invoice.travel_order_id);
  const mainInfo = isTravelInvoice ? {
    "Invoice Number": invoice.invoice_number,
    "Status": invoice.status,
    "Payment Status": invoice.payment_status || '-',
    "Invoice Type": invoice.invoice_type ? capitalizeFirstLetter(invoice.invoice_type) : '-',
    "Payment Type": invoice.payment_template_type ? capitalizeFirstLetter(invoice.payment_template_type.replaceAll('_', ' ')) : '-',
    "Payment Stage": invoice.payment_stage || '-',
    "Installment Number": invoice.installment_number || '-',
    "Order Number": invoice.order_number || (invoice.travel_order_id ? `Travel Order #${invoice.travel_order_id}` : '-'),
    "Invoice Date": invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) : '-',
    "Due Date": invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) : '-',
    "Subtotal": displaySubtotal,
    ...Object.fromEntries(travelTaxLines),
    "Total Amount": invoice.total_amount,
    "Total Order Amount": invoice.total_order_amount ?? '-',
    "Balance Amount": invoice.balance_amount ?? '-',
    "Currency": invoice.currency,
    "Customer": invoice.client_name || '-',
    "Notes": invoice.notes || '-',
  } : {
    "Invoice Number": invoice.invoice_number,
    "Status": invoice.status,
    "Billing Type": invoice.invoice_type === 'full' ? 'Full' : 'Partial',
    "Invoice Percentage": invoice.invoice_type === 'full'
      ? '100%'
      : `${invoice.percentage ? Number(invoice.percentage).toFixed(2) : '0.00'}%`,
    "Account": invoice.account_name,
    "Order Number": invoice.order_number || (invoice.sales_order_id ? `Order #${invoice.sales_order_id}` : '-'),
    "Invoice Date": invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) : '-',
    "Due Date": invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) : '-',
    "Subtotal": displaySubtotal,
    "Total Amount": invoice.total_amount,
    "Currency": invoice.currency,
    "Notes": invoice.notes || '-',
    ...(order && {
      "Total Invoiced": order.invoiced_amount !== undefined ? Number(order.invoiced_amount).toFixed(2) : undefined,
      "Remaining to Invoice": order.remaining_amount !== undefined ? Number(order.remaining_amount).toFixed(2) : undefined
    }),
    ...(gstIrn && { "IRN": gstIrn })
  };

  const travelInfo = isTravelInvoice ? {
    "Trip": invoice.trip_name || travelOrderDetail?.trip?.trip_name || travelOrderDetail?.trip?.package_name || '-',
    "Departure Date": invoice.departure_date
      ? new Date(invoice.departure_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : (travelOrderDetail?.departure_date
        ? new Date(travelOrderDetail.departure_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        : '-'),
    "Traveller Count": travelOrderDetail?.travellers ?? '-',
    "Primary Contact": travelOrderDetail?.contact_number || '-',
    "Email": travelOrderDetail?.email || '-',
    "Order Status": travelOrderDetail?.status || '-',
    "Order Payment Status": travelOrderDetail?.payment?.pending_amount > 0 ? 'Partially Paid' : 'Paid',
  } : null;

  const uploadDocument = async (file) => {
    const tenant = window.location.hostname.split('.')[0];
    const fileName = `invoice_${invoice.id}_${Date.now()}.pdf`;

    try {
      if (!file) {
        throw new Error('No file provided');
      }
      const uploadResult = await uploadPrivateObject({
        axiosInstance,
        file,
        env: process.env.REACT_APP_STORAGE_ENV,
        featurePath: 'invoices',
        scope: 'user',
        includeDatePartition: true
      });

      return {
        path: uploadResult.path,
        bucket: uploadResult.bucket,
        url: uploadResult.url,
        file: {
          name: fileName,
          size: file.size,
          type: file.type
        }
      };

    } catch (error) {
      console.error('Document upload error:', error);
      throw error;
    }
  };

  const downloadInvoice = async (invoiceId, shouldDownload = true) => {
    if (isDownloading) return;
    setIsDownloading(true);
    setDownloadLoadingId(invoiceId);
    try {
      const response = await axiosInstance.get(`/invoices/pdf/${invoiceId}`, {
        responseType: 'blob',
        timeout: 30000
      });
      if (response.data.type !== 'application/pdf') {
        throw new Error("Invalid response format - not a PDF");
      }
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });

      // If shouldDownload is true, trigger the download
      if (shouldDownload) {
        const blobUrl = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `Invoice_${invoiceId}.pdf`);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(link);
        enqueueSnackbar("Invoice downloaded successfully", { variant: "success" });
      }
      // --- Log PDF download ---
      await frontendLogger.logCreate('Invoice', {
        id: invoiceId,
        action: 'pdf_download',
        user: user?.id,
        user: user.username,
        timestamp: new Date().toISOString(),
      });
      return pdfBlob;
    } catch (error) {
      console.error("Error downloading invoice:", error);
      enqueueSnackbar(error.message || "Failed to download invoice", { variant: "error" });
      return null;
    } finally {
      setIsDownloading(false);
      setDownloadLoadingId(null);
    }
  };

  const handleFieldUpdate = (field, value) => {
    console.log(`Updating field ${field} with value:`, value);
    // Add your field update logic here if needed
  };

  const handleFieldError = (field, error) => {
    if (error) {
      setFormErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setFormErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Echo feed functions
  const fetchInvoiceName = async (invoiceId) => {
    try {
      const response = await axiosInstance.get(`/invoices/${invoiceId}`);
      return response.data.invoice_number || `Invoice ${invoiceId}`;
    } catch (error) {
      return `Invoice ${invoiceId}`;
    }
  };

  const fetchAccountName = async (accountId) => {
    try {
      const response = await axiosInstance.get(`/accounts/${accountId}`);
      return response.data.name || `Account ${accountId}`;
    } catch (error) {
      return `Account ${accountId}`;
    }
  };

  const fetchDealName = async (dealId) => {
    try {
      const response = await axiosInstance.get(`/deals/${dealId}`);
      return response.data.name || `Deal ${dealId}`;
    } catch (error) {
      return `Deal ${dealId}`;
    }
  };

  const fetchContactName = async (contactId) => {
    try {
      const response = await axiosInstance.get(`/contacts/${contactId}`);
      return response.data.name || `Contact ${contactId}`;
    } catch (error) {
      return `Contact ${contactId}`;
    }
  };

  const fetchEntityName = async (entityType, entityId) => {
    try {
      switch (entityType) {
        case 'lead':
          const leadResponse = await axiosInstance.get(`/leads/${entityId}`);
          return leadResponse.data.name || `Lead ${entityId}`;
        case 'account':
          return await fetchAccountName(entityId);
        case 'deal':
          return await fetchDealName(entityId);
        case 'contact':
          return await fetchContactName(entityId);
        case 'order':
          const orderResponse = await axiosInstance.get(`/orders/${entityId}`);
          return orderResponse.data.order_number || `Order ${entityId}`;
        case 'invoice':
          return await fetchInvoiceName(entityId);
        case 'quote':
          const quoteResponse = await axiosInstance.get(`/quotes/${entityId}`);
          return quoteResponse.data.quote_number || `Quote ${entityId}`;
        case 'product':
          const productResponse = await axiosInstance.get(`/products/${entityId}`);
          return productResponse.data.name || `Product ${entityId}`;
        case 'case':
          const caseResponse = await axiosInstance.get(`/cases/${entityId}`);
          return caseResponse.data.case_number || `Case ${entityId}`;
        default:
          return `${entityType} ${entityId}`;
      }
    } catch (error) {
      return `${entityType} ${entityId}`;
    }
  };

  const fetchUserName = async (userId) => {
    try {
      const response = await axiosInstance.get(`/users/${userId}`);
      return response.data.name || `User ${userId}`;
    } catch (error) {
      return `User ${userId}`;
    }
  };

  const fetchOrderName = async (orderId) => {
    try {
      const response = await axiosInstance.get(`/orders/${orderId}`);
      return response.data.order_number || `Order ${orderId}`;
    } catch (error) {
      return `Order ${orderId}`;
    }
  };

  const fetchQuoteName = async (quoteId) => {
    try {
      const response = await axiosInstance.get(`/quotes/${quoteId}`);
      return response.data.quote_number || `Quote ${quoteId}`;
    } catch (error) {
      return `Quote ${quoteId}`;
    }
  };

  const fetchProductName = async (productId) => {
    try {
      const response = await axiosInstance.get(`/products/${productId}`);
      return response.data.name || `Product ${productId}`;
    } catch (error) {
      return `Product ${productId}`;
    }
  };

  const fetchCaseName = async (caseId) => {
    try {
      const response = await axiosInstance.get(`/cases/${caseId}`);
      return response.data.case_number || `Case ${caseId}`;
    } catch (error) {
      return `Case ${caseId}`;
    }
  };



  const handleEchoSubmit = async (feedItem) => {
    // Add the new feed item to the beginning of the list
    setFeedItems(prevItems => [feedItem, ...prevItems]);
    
    // Close the modal
    handleCloseEchoModal();
    
    // Show success message
    setSnackbar({
      open: true,
      message: 'Message posted successfully!',
      severity: 'success',
    });
    
    // Refresh the feed items to ensure we have the latest data
    await fetchFeedItems();
  };

  const handleEditFeedItem = async (editData) => {
    try {
      setIsEditingFeed(true);
      
      // Extract the feed item ID from the edit data
      const feedItemId = editData.feed_item_id;
      
      if (!feedItemId) {
        throw new Error('No feed item ID found in edit data');
      }
      
      console.log('=== EDIT FEED ITEM DEBUG ===');
      console.log('Feed Item ID:', feedItemId);
      console.log('Edit Data received:', editData);
      console.log('Object Type:', editData.object_type);
      console.log('Object ID:', editData.object_id);
      console.log('Body content:', editData.body);
      console.log('Mentions:', editData.mentions);
      
      // Make the PUT request to the correct endpoint with the feed item ID
      const response = await axiosInstance.put(`/feed-items/${feedItemId}`, {
        feeditem: {
          object_type: editData.object_type,
          object_id: editData.object_id,
          body: editData.body
          // Don't include mentions - backend will parse them from body automatically
        }
      });
      
      if (response.status === 200) {
        // Refresh the entire feed to get updated data and proper sorting
        await fetchFeedItems();
        
        setSnackbar({
          open: true,
          message: 'Message updated successfully!',
          severity: 'success',
        });
        
        // The EchoComposer will handle clearing the edit state via onSuccess callback
        // so we don't need to do it here
      }
    } catch (error) {
      console.error('Error updating feed item:', error);
      // setSnackbar({
      //   open: true,
      //   message: 'Error updating message',
      //   severity: 'error',
      // });
    } finally {
      setIsEditingFeed(false);
    }
  };

  const handleDeleteFeedItem = async (feedItemId) => {
    try {
      await axiosInstance.delete(`/feed/${feedItemId}/`);
      setFeedItems(prev => prev.filter(item => item.id !== feedItemId));
      setDeleteConfirmItem(null);
      setIsDeleteModalOpen(false);
      setSnackbar({
        open: true,
        message: 'Message deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting feed item:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete message',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = (feedItem) => {
    setDeleteConfirmItem(feedItem);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmItem(null);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteConfirmOk = async () => {
    if (deleteConfirmItem) {
      await handleDeleteFeedItem(deleteConfirmItem.id);
    }
  };

  const startEditFeedItem = (feedItem) => {
    setEditingFeedItem(feedItem);
    setEditFeedContent(feedItem.content);
  };

  const cancelEditFeedItem = () => {
    setEditingFeedItem(null);
    setEditFeedContent('');
  };

  const handleOpenEchoModal = () => {
    setIsEchoModalOpen(true);
  };

  const handleCloseEchoModal = () => {
    setIsEchoModalOpen(false);
    setNewEchoContent('');
  };

  const handleCreateEcho = async () => {
    if (!newEchoContent.trim()) return;

    const feedItem = {
      entity_type: 'invoice',
      entity_id: id,
      content: newEchoContent,
      created_by: user.id
    };

    await handleEchoSubmit(feedItem);
  };

  // Component for rendering mentions with immediate name display
  const MentionDisplay = ({ mention, fullMatch, entityTypeOrUser, entityId, mentions, navigate }) => {
    // Calculate display name immediately without async operations
    let displayName = '';
    
    if (entityId) {
      // For entity mentions, we'll need to fetch the name
      // But for now, let's show the entity type and ID
      displayName = `${entityTypeOrUser}:${entityId}`;
    } else {
      // For user mentions, use entity_name immediately if available
      if (mention && mention.entity_name) {
        displayName = mention.entity_name;
      } else {
        // Fallback to username
        displayName = entityTypeOrUser;
      }
    }

    return (
      <Box
        component="span"
        sx={{
          color: entityId ? 'info.main' : 'primary.main',
          fontWeight: 'bold',
          backgroundColor: entityId ? 'rgba(33, 150, 243, 0.08)' : 'rgba(103, 58, 183, 0.08)',
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          mx: 0.25,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: entityId ? 'rgba(33, 150, 243, 0.15)' : 'rgba(103, 58, 183, 0.15)',
          }
        }}
        onClick={() => {
          if (entityId) {
            navigate(`/${entityTypeOrUser.toLowerCase()}s/${entityId}`);
          } else {
            if (mention && mention.entity_id) {
              navigate(`/users/${mention.entity_id}/profile`);
            }
          }
        }}
      >
        {displayName}
      </Box>
    );
  };

  // Helper function to extract name parts from created_by_name
  const extractNameParts = (createdByUser, createdByName) => {
    let nameParts = {
      firstName: 'U',
      middleName: '',
      lastName: '',
      avatarUrl: null
    };

    if (createdByUser?.first_name && createdByUser?.last_name) {
      nameParts = {
        firstName: createdByUser.first_name,
        middleName: createdByUser.middle_name || '',
        lastName: createdByUser.last_name,
        avatarUrl: createdByUser.avatar || null
      };
    } else if (createdByName) {
      const namePartsArray = createdByName.split(' ').filter(part => part.trim());
      if (namePartsArray.length === 1) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: '',
          lastName: '',
          avatarUrl: null
        };
      } else if (namePartsArray.length >= 2) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: namePartsArray.slice(1, -1).join(' '),
          lastName: namePartsArray[namePartsArray.length - 1],
          avatarUrl: null
        };
      }
    }
    
    return nameParts;
  };

  // Enhanced feed content parser that uses async name fetching
  const parseFeedContent = (feedItem) => {
    if (!feedItem?.feeditem?.body) return '';
    
    const body = feedItem.feeditem.body;
    const mentions = feedItem.feeditem.mentions || [];
    
    console.log('Parsing feed content:', { body, mentions });
    
    // Parse mentions in the format @username (for users) or @EntityType:entityId (for entities)
    const parts = [];
    const mentionRegex = /@([A-Za-z0-9_.@]+)(?::([A-Za-z0-9_]+))?/g;
    let lastIndex = 0;
    let match;
    
    // Create a map of usernames to mention data for better matching
    const userMentionsMap = {};
    mentions.forEach(mention => {
      if (mention.type === 'user' && mention.entity_name) {
        // Try to match by username pattern in the body
        const usernamePattern = new RegExp(`@${mention.entity_name.replace(/\s+/g, '[._]')}`, 'i');
        if (usernamePattern.test(body)) {
          userMentionsMap[mention.entity_name] = mention;
        }
      }
    });
    
    while ((match = mentionRegex.exec(body)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {body.substring(lastIndex, match.index)}
          </span>
        );
      }
      
      // Add mention as highlighted text with immediate name display
      const [fullMatch, firstPart, secondPart] = match;
      console.log('Found mention:', { fullMatch, firstPart, secondPart }); // Debugging
      
      // Determine if this is a user mention or entity mention
      let entityTypeOrUser, entityId;
      if (secondPart) {
        // Format: @EntityType:entityId
        entityTypeOrUser = firstPart;
        entityId = secondPart;
      } else {
        // Format: @username (user mention)
        entityTypeOrUser = firstPart;
        entityId = null;
      }
      
      // Find the corresponding mention data
      let mentionData;
      if (entityId) {
        // For entity mentions: @EntityType:entityId
        mentionData = mentions.find(m => m.type === 'record' && m.entity_type === entityTypeOrUser && m.entity_id === entityId);
      } else {
        // For user mentions: @username - try multiple matching strategies
        // First, try exact match with entity_name
        mentionData = mentions.find(m => m.type === 'user' && m.entity_name === entityTypeOrUser);
        
        // If not found, try matching by username pattern
        if (!mentionData) {
          const userMention = mentions.find(m => {
            if (m.type === 'user' && m.entity_name) {
              // Check if the username in text matches the entity_name (with possible dots/underscores)
              const normalizedEntityName = m.entity_name.replace(/\s+/g, '[._]');
              const usernamePattern = new RegExp(`^${normalizedEntityName}$`, 'i');
              return usernamePattern.test(entityTypeOrUser);
            }
            return false;
          });
          if (userMention) {
            mentionData = userMention;
          }
        }
        
        // If still not found, try to find by position in the mentions array
        if (!mentionData) {
          const userMentions = mentions.filter(m => m.type === 'user');
          // Count how many user mentions we've seen so far in the text
          const textBeforeCursor = body.substring(0, match.index);
          const userMentionsBefore = (textBeforeCursor.match(/@[A-Za-z0-9_.@]+/g) || []).length;
          if (userMentionsBefore < userMentions.length) {
            mentionData = userMentions[userMentionsBefore];
          }
        }
      }
      
      console.log('Found mention data:', mentionData); // Debugging
      parts.push(
        <MentionDisplay
          key={`mention-${match.index}`}
          mention={mentionData} // Pass mentionData directly
          fullMatch={fullMatch}
          entityTypeOrUser={entityTypeOrUser}
          entityId={entityId}
          mentions={mentions}
          navigate={navigate}
        />
      );
      
      lastIndex = match.index + fullMatch.length;
    }
    
    // Add remaining text
    if (lastIndex < body.length) {
      parts.push(
        <span key={`text-end`}>
          {body.substring(lastIndex)}
        </span>
      );
    }
    
    return parts;
  };

  // Shared by the classic and v2 layouts below: the same actions, field
  // sections and side column render in both.
  const recordActions = (
    <>
      <Tooltip title={invoice?.is_gst_invoice_generated ? "Cannot edit invoice after GST e-invoice generation" : "Edit Invoice"}>
        <span>
          <IconButton
            onClick={() => handleEditInvoice(invoice.id)}
            disabled={isEditLoading || isInvoiceCancelled || invoice?.is_gst_invoice_generated}
            sx={{
              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
              borderRadius: '50%',
              padding: '8px',
              '&:hover': {
                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                transform: 'translateY(-2px)',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
              },
              transition: 'all 0.2s ease-in-out',
              opacity: (isInvoiceCancelled || invoice?.is_gst_invoice_generated) ? 0.5 : 1,
            }}
          >
            {isEditLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <EditIcon sx={{ color: '#fff' }} />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Send Email">
        <span>
          <IconButton
            onClick={handleToggleEmailModal}
            disabled={isEmailLoading || isInvoiceCancelled}
            sx={{
              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
              borderRadius: '50%',
              padding: '8px',
              '&:hover': {
                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                transform: 'translateY(-2px)',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
              },
              transition: 'all 0.2s ease-in-out',
              opacity: isInvoiceCancelled ? 0.5 : 1,
            }}
          >
            {isEmailLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <EmailIcon sx={{ color: '#fff' }} />}
          </IconButton>
        </span>
      </Tooltip>
      {canRequestPayment && (
        <Tooltip title={paymentLinkDisabledReason || 'Send payment link'}>
          <span>
            <IconButton
              onClick={() => openPaymentLinkModal(invoice?.id, 'Send payment link')}
              disabled={!hasPaymentGateway}
              sx={{
                background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                borderRadius: '50%',
                padding: '8px',
                '&:hover': {
                  background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.2s ease-in-out',
                opacity: !hasPaymentGateway ? 0.5 : 1,
              }}
            >
              <PaymentIcon sx={{ color: '#fff' }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Download PDF">
        <span>
          <IconButton
            onClick={handleDownloadInvoice}
            disabled={isDownloading || isInvoiceCancelled}
            sx={{
              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
              borderRadius: '50%',
              padding: '8px',
              '&:hover': {
                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                transform: 'translateY(-2px)',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
              },
              transition: 'all 0.2s ease-in-out',
              opacity: isInvoiceCancelled ? 0.5 : 1,
            }}
          >
            {isDownloading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <PictureAsPdfIcon sx={{ color: '#fff' }} />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Generate GST E-Invoice">
        <span>
          <IconButton
            onClick={() => setIsGstDialogOpen(true)}
            disabled={isInvoiceCancelled || isGstGenerating || invoice.currency !== 'INR'}
            sx={{
              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
              borderRadius: '50%',
              padding: '8px',
              '&:hover': {
                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                transform: 'translateY(-2px)',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
              },
              transition: 'all 0.2s ease-in-out',
              opacity: isInvoiceCancelled || isGstGenerating || invoice.currency !== 'INR' ? 0.5 : 1,
              display: invoice.currency !== 'INR' ? 'none' : 'flex',
            }}
          >
            {isGstGenerating ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : (
              <ReceiptLongIcon sx={{ color: '#fff' }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      {invoice.is_gst_invoice_generated && !invoice.is_cancelled && (
        <Tooltip title="Cancel GST E-Invoice">
          <span>
            <IconButton
              onClick={() => setIsCancelDialogOpen(true)}
              disabled={isInvoiceCancelled}
              sx={{
                background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                borderRadius: '50%',
                padding: '8px',
                '&:hover': {
                  background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.2s ease-in-out',
                opacity: isInvoiceCancelled ? 0.5 : 1,
              }}
            >
              <CancelIcon sx={{ color: '#fff' }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  );
  const recordDetailsBody = (
    <>
      {visibleSections.map(sectionLabel => {
        if (sectionLabel === 'Invoice Information') {
          return renderDetailCard('Invoice Information', mainInfo);
        }
        if (sectionLabel === 'Travel Information' && travelInfo) {
          return renderDetailCard('Travel Information', travelInfo);
        }
        if (sectionLabel === 'Shipping Details' || sectionLabel === 'Transport Details') {
          return renderDetailCard('Shipping Details', {
            "Transporter ID": invoice.transid || '-',
            "Transporter Name": invoice.transname ? capitalizeFirstLetter(invoice.transname) : '-',
            "Transport Mode": (() => {
              const modeMapping = {
                '1': 'Road',
                '2': 'Rail',
                '3': 'Air',
                '4': 'Ship',
              };
              const mode = modeMapping[invoice.transmode] || invoice.transmode || '-';
              return mode !== '-' ? capitalizeFirstLetter(mode) : mode;
            })(),
            ...(invoice.transmode === '1' && {
              "Vehicle Number": invoice.vehno || '-',
              "Vehicle Type": (() => {
                const typeMapping = {
                  'R': 'Regular',
                  'C': 'Container',
                  'O': 'Over Dimensional Cargo',
                };
                const type = typeMapping[invoice.vehtype] || invoice.vehtype || '-';
                return type !== '-' ? capitalizeFirstLetter(type) : type;
              })(),
            }),
            ...(invoice.transmode === '4' && {
              "Vehicle Number": invoice.vehno || '-',
              "Transport Document Number": invoice.transdocno || '-',
              "Transport Document Date": invoice.transdocdate ? new Date(invoice.transdocdate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : '-',
            }),
            ...(['2', '3'].includes(invoice.transmode) && {
              "Transport Document Number": invoice.transdocno || '-',
              "Transport Document Date": invoice.transdocdate ? new Date(invoice.transdocdate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : '-',
            }),
          });
        }
        if (sectionLabel === 'Invoice Items') {
          return renderInvoiceItemsTable();
        }
        if (sectionLabel === 'Payment Schedule') {
          if (!(paymentSchedule.length > 0 || canRequestPayment)) return null;
          return (
            <Card
              key="Payment Schedule"
              sx={{ padding: '30px', boxShadow: 3, borderRadius: '8px', backgroundColor: '#fff', margin: '20px' }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <MDTypography variant="h6" fontWeight="medium">
                  Payment schedule
                </MDTypography>
                {canRequestPayment && (
                  <Tooltip title={paymentLinkDisabledReason || 'Email customer a Stripe payment link'}>
                    <span>
                      <MDButton
                        variant="gradient"
                        color="info"
                        size="small"
                        startIcon={<PaymentIcon />}
                        onClick={() => openPaymentLinkModal(invoice?.id, 'Send payment link')}
                        disabled={!hasPaymentGateway}
                      >
                        Send payment link
                      </MDButton>
                    </span>
                  </Tooltip>
                )}
              </MDBox>
              {paymentSchedule.length > 0 ? (
                <PaymentScheduleList
                  rows={paymentSchedule}
                  highlightInvoiceId={id}
                  resendDisabledReason={paymentLinkDisabledReason}
                  onResendPaymentEmail={(row) =>
                    openPaymentLinkModal(row.invoice_id, 'Resend payment email')
                  }
                />
              ) : (
                <MDTypography variant="body2" color="text">
                  No payment installments recorded yet. Use Send payment link to request payment for this invoice.
                </MDTypography>
              )}
            </Card>
          );
        }
        if (sectionLabel === 'Custom Field Information' && customFields && customFields.length > 0) {
          return renderDetailCard('Custom Field Information', [], renderCustomFields());
        }
        if (sectionLabel === 'Audit Information') {
          return (
            <MDBox key="Audit Information">
              <Card
                sx={{
                  padding: '30px',
                  boxShadow: 3,
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  margin: '20px',
                }}
              >
                <MDTypography variant="h6" fontWeight="medium">
                  Audit Information
                </MDTypography>
                <MDBox pt={3}>
                  <MDBox>
                    <Grid container spacing={3}>
                      {auditFields.map((item) => (
                        <Grid item xs={12} md={6} key={item.field}>
                          <MDBox display="flex" alignItems="center">
                            <MDTypography
                              variant="button"
                              fontWeight="medium"
                              color="text"
                              sx={{ marginRight: '4px', flexShrink: 0 }}
                            >
                              {item.label}:
                            </MDTypography>
                            {item.userIdField ? (
                              <Link
                                to={`/users/${invoice[item.userIdField]}/profile`}
                                style={{ textDecoration: 'none', color: 'inherit' }}
                              >
                                <MDTypography color="info" sx={{ fontSize: '0.9rem', fontWeight: 'medium' }}>
                                  {(() => {
                                    const value = invoice[item.field] || ' ';
                                    if (typeof value === 'string' && value && !item.field?.includes('email')) {
                                      return capitalizeFirstLetter(value);
                                    }
                                    return value;
                                  })()}
                                </MDTypography>
                              </Link>
                            ) : (
                              <MDTypography color="text" sx={{ fontSize: '0.9rem', fontWeight: 'regular' }}>
                                {(() => {
                                  const value = item.field === 'created_at' || item.field === 'updated_at'
                                    ? formatDate(invoice[item.field])
                                    : invoice[item.field] || ' ';
                                  if (typeof value === 'string' && value &&
                                    !item.field?.includes('email') &&
                                    item.field !== 'created_at' &&
                                    item.field !== 'updated_at') {
                                    return capitalizeFirstLetter(value);
                                  }
                                  return value;
                                })()}
                              </MDTypography>
                            )}
                          </MDBox>
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          );
        }
        return null;
      })}
    </>
  );
  // Echo feed, named so the v2 record layout can place it in a tab;
  // recordSideColumn below keeps the classic order.
  const invoiceEchoFeedCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RecordVoiceOverIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Echo Feed
            </MDTypography>
          </Box>
          <MDButton
            variant="gradient" color="dark"
            onClick={handleOpenEchoModal}
          >
            Add Message
          </MDButton>
        </MDBox>

        <MDBox p={2}>
          {/* EchoComposer removed, replaced by modal */}
          <Modal
            open={isEchoModalOpen}
            onClose={handleCloseEchoModal}
            aria-labelledby="echo-modal-title"
            aria-describedby="echo-modal-description"
          >
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              bgcolor: 'background.paper',
              boxShadow: 24,
              borderRadius: 2,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <MDTypography id="echo-modal-title" variant="h6" mb={2}>
                Post a Feed
              </MDTypography>
              <EchoComposer
                placeholder="Type @ to mention users..."
                axiosInstance={axiosInstance}
                objectType="invoices"
                objectId={id}
                onSubmit={handleEchoSubmit}
                onCancel={handleCloseEchoModal}
                onSuccess={(message) => {
                  setSnackbar({
                    open: true,
                    message: 'Feed item created successfully',
                    severity: 'success',
                  });
                  handleCloseEchoModal();
                  fetchFeedItems(); // Refresh the feed items
                }}
                onError={(message) => setSnackbar({
                  open: true,
                  message: `Error creating feed item: ${message}`,
                  severity: 'error',
                })}
              />
            </Box>
          </Modal>
          {/* Display feed items */}
          <Box mt={3}>
            {isFeedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} color="info" />
              </Box>
            ) : feedItems.length > 0 ? (
              <Box className="pearl-echo-feed" sx={{ 
                maxHeight: 400, 
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa'
              }}>
                {feedItems.map((item, index) => (
                  <Box 
                    key={item.id} 
                    sx={{ 
                      p: 2, 
                      borderBottom: index < feedItems.length - 1 ? '1px solid #e0e0e0' : 'none',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: '#f8f9fa'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <MDUserAvatar
                        firstName={extractNameParts(item.created_by_user, item.created_by_name).firstName}
                        middleName={extractNameParts(item.created_by_user, item.created_by_name).middleName}
                        lastName={extractNameParts(item.created_by_user, item.created_by_name).lastName}
                        avatarUrl={extractNameParts(item.created_by_user, item.created_by_name).avatarUrl}
                        size={36}
                        variant="circular"
                        sx={{ mr: 2, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0, ml:2  }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              variant="subtitle2" 
                              fontWeight="medium"
                              sx={{ 
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => {
                                // Navigate to user profile
                                const userMention = item.feeditem.mentions?.find(m => m.type === 'user' && m.entity_name === item.created_by_name);
                                if (userMention) {
                                  navigate(`/users/${userMention.entity_id}/profile`);
                                }
                              }}
                            >
                              {item.created_by_name}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ ml: 1 }}
                            >
                              {formatDate(item.updated_at, 'datetime')}
                            </Typography>
                          </Box>

                          {/* Edit/Delete buttons - only show for current user's messages */}
                          {item.created_by === user?.id && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Edit message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="info"
                                  onClick={() => startEditFeedItem(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                >
                                  <EditIcon fontSize="small" />
                                </MDButton>
                              </Tooltip>
                              <Tooltip title="Delete message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="error"
                                  onClick={() => handleDeleteConfirm(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </MDButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        {editingFeedItem === item.id ? (
                          <Box sx={{ mt: 1 }}>
                            <EchoComposer
                              value={editFeedContent}
                              onChange={setEditFeedContent}
                              placeholder="Edit your message..."
                              axiosInstance={axiosInstance}
                              objectType="invoices"
                              objectId={id}
                              feedItemId={item.id}
                              isEditMode={true}
                              onSubmit={handleEditFeedItem}
                              onCancel={cancelEditFeedItem}
                              onSuccess={() => {
                                // Auto-save when user clicks the Update button in EchoComposer
                                setEditingFeedItem(null);
                                setEditFeedContent('');
                              }}
                              sx={{ 
                                mb: 1,
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'white'
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.5,
                              color: 'text.primary'
                            }}
                          >
                            {parseFeedContent(item)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: -2,
              }}>
                <Box sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.04)',
                    mb: 1.5 }}>
                    <RecordVoiceOverIcon sx={{ color: 'text.secondary', fontSize: 24,}} />
                  </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  No messages yet
                </Typography>
                 {/* <Typography variant="caption">
                  Start a conversation by typing a message above
                </Typography> */}
              </Box>
            )}
          </Box>
        </MDBox>
      </Card>
    </>
  );

  const recordSideColumn = (
    <>
      {renderAttachmentsCard()}

      {/* Add margin between the two sections */}
      <Box mt={5} />
      {invoiceEchoFeedCard}
    </>
  );

  // v2: the invoice laid out like Account 360 (components/v2/record). The
  // pieces are the ones the classic page renders; line items and the payment
  // schedule stay in the field sections on Overview.
  const invoiceTotalNum = parseFloat(invoice?.total_amount) || 0;
  const invoiceBalanceNum = invoice?.balance_amount != null && invoice?.balance_amount !== '' ? parseFloat(invoice.balance_amount) || 0 : null;
  const invoicePaid = /^paid$/i.test(invoice?.payment_status || '');
  const invoiceOverdue = !invoicePaid && invoice?.due_date && new Date(invoice.due_date) < new Date(new Date().toDateString());
  // The stored balance reads 0 on some unpaid invoices; show Paid and Balance
  // only when they agree with the payment status.
  const showInvoiceBalance = invoiceBalanceNum != null && (invoicePaid || invoiceBalanceNum > 0);
  const v2Main = (
    <MDBox>
      <RecordPageV2
        sectionsLabel="Invoice sections"
        hero={{
          label: 'Invoice summary',
          icon: Receipt,
          eyebrow: 'Invoice',
          title: invoice?.invoice_number || '',
          actions: recordActions,
          badges: (
            <>
              {invoice?.status && <StatusBadge status={invoice.status} />}
              {invoice?.payment_status && <StatusBadge status={invoice.payment_status} />}
              {invoiceOverdue && <HeroBadge>Overdue</HeroBadge>}
            </>
          ),
          meta: [
            { key: 'account', icon: Building2, content: invoice?.account_name && invoice?.account_id ? (
              <Link to={`/accounts/${invoice.account_id}`} style={{ color: 'inherit', fontWeight: 600 }}>{invoice.account_name}</Link>
            ) : invoice?.account_name || null },
            { key: 'order', icon: Package, content: invoice?.order_number && invoice?.sales_order_id ? (
              <Link to={`/orders/${invoice.sales_order_id}`} style={{ color: 'inherit', fontWeight: 600 }}>{`Order ${invoice.order_number}`}</Link>
            ) : invoice?.order_number ? `Order ${invoice.order_number}` : null },
            { key: 'issued', icon: CalendarDays, content: invoice?.invoice_date ? `Issued ${formatDate(invoice.invoice_date, 'date')}` : null },
          ],
        }}
        tiles={[
          { key: 'total', icon: CircleDollarSign, label: 'Total', value: invoice?.total_amount != null && invoice?.total_amount !== '' ? formatCurrency(invoiceTotalNum, invoice.currency) : '—' },
          showInvoiceBalance ? { key: 'paid', icon: CheckSquare, label: 'Paid', value: formatCurrency(Math.max(0, invoiceTotalNum - invoiceBalanceNum), invoice.currency) } : null,
          showInvoiceBalance ? { key: 'balance', icon: Wallet, label: 'Balance', value: formatCurrency(invoiceBalanceNum, invoice.currency) } : null,
          { key: 'payment', icon: CircleDot, label: 'Payment', value: invoice?.payment_status || '—' },
          { key: 'due', icon: CalendarClock, label: 'Due date', value: invoice?.due_date ? formatDate(invoice.due_date, 'date') : '—', note: invoiceOverdue ? 'Overdue' : null },
        ]}
        tabs={[
          {
            key: 'overview',
            label: 'Overview',
            icon: LayoutGrid,
            render: () => (
              <Card>
                <MDBox p={3}>
                  {recordDetailsBody}
                </MDBox>
              </Card>
            ),
          },
          { key: 'activity', label: 'Activity', icon: History, render: () => invoiceEchoFeedCard },
          { key: 'files', label: 'Files', icon: Paperclip, count: attachments.length, render: () => renderAttachmentsCard() },
        ]}
        aside={(selectTab) => (
          <ActivityTimeline feedItems={feedItems} onOpenAll={() => selectTab('activity')} emptyText="No activity on this invoice yet." />
        )}
      />
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? v2Main : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                  <MDTypography variant="h6" color="white">
                    Invoice Details: {invoice?.invoice_number}
                  </MDTypography>
                  <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    {recordActions}
                  </MDBox>
                </MDBox>
              </MDBox>
              <MDBox>
                {recordDetailsBody}
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            {recordSideColumn}
          </Grid>
        </Grid>
      </MDBox>
      )}
      {/* Edit Invoice Modal (copied from Invoices.js) */}
      <Dialog
        open={openEditInvoiceModal}
        onClose={() => setOpenEditInvoiceModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            width: '1200px',
            maxWidth: '100%'
          }
        }}
      >
        <Box className="pearl-popup-header"
          sx={{
            backgroundColor: colors.popupHeader.background,
            px: 3,
            py: 2.5,
          }}
        >
          <MDTypography
            variant="h6"
            component="h2"
            sx={{ color: '#FFFFFF' }}
          >
            Edit Invoice
          </MDTypography>
        </Box>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={accounts}
                getOptionLabel={(option) => option?.label || ''}
                value={accounts.find(acc => acc.value === editedInvoice?.account_id) || null}
                onChange={(e, newValue) => {
                  handleInvoiceFieldChange('account_id', newValue?.value);
                  handleInvoiceFieldChange('account_name', newValue?.label);
                  handleInvoiceFieldChange('currency', newValue?.currency);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Account"
                    fullWidth
                    margin="normal"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "45px"
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Invoice Date"
                type="date"
                value={editedInvoice?.invoice_date || ''}
                onChange={(e) => handleInvoiceFieldChange('invoice_date', e.target.value)}
                fullWidth
                margin="normal"
                disabled={false}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Due Date"
                type="date"
                value={editedInvoice?.due_date || ''}
                onChange={(e) => handleInvoiceFieldChange('due_date', e.target.value)}
                fullWidth
                margin="normal"
                disabled={false}
                inputProps={{
                  min: editedInvoice?.invoice_date || undefined,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                error={
                  Boolean(
                    editedInvoice?.invoice_date &&
                    editedInvoice?.due_date &&
                    editedInvoice.due_date < editedInvoice.invoice_date
                  )
                }
                helperText={
                  editedInvoice?.invoice_date &&
                  editedInvoice?.due_date &&
                  editedInvoice.due_date < editedInvoice.invoice_date
                    ? 'Due date cannot be earlier than invoice date'
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={['Draft', 'Sent', 'Completed', 'Cancelled']}
                value={editedInvoice?.status || ''}
                onChange={(e, newValue) => handleInvoiceFieldChange('status', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Invoice Status"
                    fullWidth
                    margin="normal"
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '45px',
                      },
                    }}
                    disabled={false}
                  />
                )}
              />
            </Grid>
            <Grid item md={12} xs={6} >
              <TextField
                label="Notes"
                value={editedInvoice?.notes || ''}
                onChange={(e) => handleInvoiceFieldChange('notes', e.target.value)}
                fullWidth
                multiline
                margin="normal"
                disabled={false}
              />
            </Grid>

            {/* Shipping Details Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <MDTypography variant="subtitle2" fontWeight="medium" mb={2}>
                Shipping Details
              </MDTypography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Transporter ID"
                value={editedInvoice?.transid || ''}
                onChange={(e) => handleInvoiceFieldChange('transid', e.target.value)}
                fullWidth
                margin="normal"
                disabled={false}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Transporter Name"
                value={editedInvoice?.transname || ''}
                onChange={(e) => handleInvoiceFieldChange('transname', e.target.value)}
                fullWidth
                margin="normal"
                disabled={false}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={[
                  { label: 'Road', value: '1' },
                  { label: 'Rail', value: '2' },
                  { label: 'Air', value: '3' },
                  { label: 'Ship', value: '4' },
                ]}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    const mapping = {
                      '1': 'Road',
                      '2': 'Rail',
                      '3': 'Air',
                      '4': 'Ship',
                    };
                    return mapping[option] || option;
                  }
                  return option.label;
                }}
                value={
                  editedInvoice?.transmode
                    ? (typeof editedInvoice.transmode === 'string' && ['1', '2', '3', '4'].includes(editedInvoice.transmode))
                      ? {
                        label: {
                          '1': 'Road',
                          '2': 'Rail',
                          '3': 'Air',
                          '4': 'Ship',
                        }[editedInvoice.transmode],
                        value: editedInvoice.transmode,
                      }
                      : editedInvoice.transmode
                    : null
                }
                onChange={(e, newValue) => {
                  handleInvoiceFieldChange('transmode', newValue ? newValue.value : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Transport Mode"
                    fullWidth
                    margin="normal"
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '45px',
                      },
                    }}
                    disabled={false}
                  />
                )}
              />
            </Grid>

            {/* Conditional rendering based on transport mode */}
            {editedInvoice?.transmode === '1' && ( // For Road
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Vehicle Number"
                    value={editedInvoice?.vehno || ''}
                    onChange={(e) => handleInvoiceFieldChange('vehno', e.target.value)}
                    fullWidth
                    margin="normal"
                    helperText="Enter vehicle number like KA331234 (no spaces)"
                    inputProps={{
                      pattern: "[A-Z]{2}[0-9]{6}",
                      style: { textTransform: 'uppercase' },
                    }}
                    required
                    disabled={false}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    options={[
                      { label: 'Regular', value: 'R' },
                      { label: 'Container', value: 'C' },
                      { label: 'Over Dimensional Cargo', value: 'O' },
                    ]}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') {
                        const mapping = {
                          'R': 'Regular',
                          'C': 'Container',
                          'O': 'Over Dimensional Cargo',
                        };
                        return mapping[option] || option;
                      }
                      return option.label;
                    }}
                    value={
                      editedInvoice?.vehtype
                        ? (typeof editedInvoice.vehtype === 'string' && ['R', 'C', 'O'].includes(editedInvoice.vehtype))
                          ? {
                            label: {
                              'R': 'Regular',
                              'C': 'Container',
                              'O': 'Over Dimensional Cargo',
                            }[editedInvoice.vehtype],
                            value: editedInvoice.vehtype,
                          }
                          : editedInvoice.vehtype
                        : null
                    }
                    onChange={(e, newValue) => {
                      handleInvoiceFieldChange('vehtype', newValue ? newValue.value : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vehicle Type"
                        fullWidth
                        margin="normal"
                        sx={{
                          '& .MuiInputBase-root': {
                            height: '45px',
                          },
                        }}
                        disabled={false}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
            {['2', '3'].includes(editedInvoice?.transmode) && ( // For Rail or Air
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Transport Document Number"
                    value={editedInvoice?.transdocno || ''}
                    onChange={(e) => handleInvoiceFieldChange('transdocno', e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={false}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Transport Document Date"
                    type="date"
                    value={editedInvoice?.transdocdate || ''}
                    onChange={(e) => handleInvoiceFieldChange('transdocdate', e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={false}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0],
                      min: new Date().toISOString().split('T')[0]
                    }}
                  />
                </Grid>
              </>
            )}
            {editedInvoice?.transmode === '4' && ( // For Ship
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Vehicle Number"
                    value={editedInvoice?.vehno || ''}
                    onChange={(e) => handleInvoiceFieldChange('vehno', e.target.value)}
                    fullWidth
                    margin="normal"
                    helperText="Enter vehicle number like KA331234 (no spaces)"
                    inputProps={{
                      pattern: "[A-Z]{2}[0-9]{6}",
                      style: { textTransform: 'uppercase' },
                    }}
                    disabled={false}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Transport Document Number"
                    value={editedInvoice?.transdocno || ''}
                    onChange={(e) => handleInvoiceFieldChange('transdocno', e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={false}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Transport Document Date"
                    type="date"
                    value={editedInvoice?.transdocdate || ''}
                    onChange={(e) => handleInvoiceFieldChange('transdocdate', e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={false}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0],
                      min: new Date().toISOString().split('T')[0]
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <MDBox
            sx={{
              mt: 3,
              mb: 3,
              border: "1px solid #dee2e6",
              borderRadius: "0.75rem",
              overflow: "auto",
              maxHeight: "400px"
            }}
          >
            {/* Header */}
            <MDBox
              sx={{
                display: "flex",
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #dee2e6",
                position: "sticky",
                top: 0,
                zIndex: 1
              }}
            >
              <MDBox width="20%" p={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Product Name
                </MDTypography>
              </MDBox>
              <MDBox width="13%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Unit Price
                </MDTypography>
              </MDBox>
              <MDBox width="8%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Tax %
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Discount %
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Discounted Price
                </MDTypography>
              </MDBox>
              <MDBox width="10%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Quantity
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Total Net Price
                </MDTypography>
              </MDBox>
              <MDBox width="9%" p={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Actions
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* Body */}
            <MDBox>
              {editedInvoiceItems
                .filter(item => !item.markedForDeletion)
                .map((item, index) => {
                  const discountedPrice = (item.unit_price || 0) - ((item.unit_price || 0) * (item.discount_percentage || 0) / 100);
                  const totalNetPrice = discountedPrice * (item.quantity || 0);
                  const totalPriceWithTax = totalNetPrice * (1 + (item.tax || 0) / 100);
                  return (
                    <MDBox
                      key={item.tempId}
                      display="flex"
                      sx={{
                        borderBottom: "1px solid #dee2e6",
                        '&:hover': { backgroundColor: '#f8f9fa' }
                      }}
                    >
                      <MDBox width="20%" p={2}>
                        <Autocomplete
                          options={productList}
                          getOptionLabel={(option) => option.name}
                          value={productList.find((product) => product.name === item.product_name) || null}
                          onChange={(e, newValue) =>
                            handleInvoiceItemChange(item.tempId, 'product_name', newValue ? newValue.name : '')
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Product Name"
                              variant="outlined"
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': { borderColor: '#dee2e6' },
                                  '&:hover fieldset': { borderColor: '#7b809a' }
                                }
                              }}
                            />
                          )}
                          disabled={false}
                        />
                      </MDBox>
                      <MDBox width="13%" p={2} textAlign="right">
                        {editingPriceIndex === index ? (
                          <TextField
                            value={editingPriceValue}
                            onChange={(e) => {
                              setEditingPriceValue(e.target.value);
                              handleInvoiceItemChange(item.tempId, 'unit_price', parseFloat(e.target.value) || 0);
                            }}
                            onBlur={() => {
                              setEditingPriceIndex(null);
                              setEditingPriceValue('');
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                setEditingPriceIndex(null);
                                setEditingPriceValue('');
                              }
                            }}
                            size="small"
                            sx={{
                              width: '100%',
                              '& .MuiInputBase-root': {
                                height: '32px',
                                fontSize: '0.875rem'
                              }
                            }}
                            inputProps={{
                              type: 'number',
                              step: '0.01',
                              min: '0',
                              style: { textAlign: 'center' }
                            }}
                            autoFocus
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <MDTypography variant="button" fontWeight="regular">
                              {formatCurrency(item.unit_price || 0, editedInvoice?.currency)}
                            </MDTypography>
                            <Tooltip title="Edit Unit Price">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingPriceIndex(index);
                                  setEditingPriceValue(item.unit_price?.toString() || '');
                                }}
                                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' }, width: '24px', height: '24px' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </MDBox>
                      <MDBox width="8%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {item.tax}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="12%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {item.discount_percentage}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="12%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {formatCurrency(parseFloat(discountedPrice), editedInvoice?.currency)}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="10%" p={2} textAlign="right">
                        <TextField
                          value={item.quantity}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') {
                              handleInvoiceItemChange(item.tempId, 'quantity', '');
                              return;
                            }
                            const parsed = parseInt(raw, 10);
                            handleInvoiceItemChange(
                              item.tempId,
                              'quantity',
                              Number.isNaN(parsed) ? '' : parsed
                            );
                          }}
                          type="number"
                          size="small"
                          disabled={false}
                          inputProps={{ min: 1, step: 1 }}
                          error={item.quantity !== '' && Number(item.quantity) <= 0}
                          sx={{
                            width: '70px',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#dee2e6' },
                              '&:hover fieldset': { borderColor: '#7b809a' }
                            }
                          }}
                        />
                      </MDBox>
                      <MDBox width="12%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {formatCurrency(totalNetPrice, editedInvoice?.currency)}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="9%" p={2} display="flex" alignItems="center">
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveInvoiceItem(item.tempId)}
                          disabled={editedInvoiceItems.filter(i => !i.markedForDeletion).length <= 1}
                          size="small"
                        >
                          <RemoveIcon />
                        </IconButton>
                        <IconButton
                          color="info"
                          onClick={handleAddInvoiceItem}
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                      </MDBox>
                    </MDBox>
                  );
                })}
            </MDBox>
          </MDBox>

          <MDBox
            sx={{
              mt: 3,
              p: 2,
              borderTop: '2px solid #dee2e6',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <MDBox sx={{ minWidth: '300px' }}>
              {(() => {
                const { subtotal, grandTotal, taxSummary } = calculateTotalAmount();
                const currency = editedInvoice?.currency || 'INR';

                return (
                  <MDBox>
                    <MDBox display="flex" justifyContent="space-between" mb={1}>
                      <MDTypography variant="button" color="text" fontWeight="regular">
                        Subtotal:
                      </MDTypography>
                      <MDTypography variant="button" color="text" fontWeight="medium">
                        {formatCurrency(subtotal, currency)}
                      </MDTypography>
                    </MDBox>

                    {Object.entries(taxSummary).map(([taxRate, taxAmount]) => (
                      <MDBox key={taxRate} display="flex" justifyContent="space-between" mb={1}>
                        <MDTypography variant="button" color="text" fontWeight="regular">
                          Tax ({taxRate}%):
                        </MDTypography>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          {formatCurrency(taxAmount, currency)}
                        </MDTypography>
                      </MDBox>
                    ))}

                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      mt={2}
                      pt={1.5}
                      sx={{ borderTop: '1px solid #dee2e6' }}
                    >
                      <MDTypography variant="button" color="dark" fontWeight="medium">
                        Total Amount:
                      </MDTypography>
                      <MDTypography variant="button" color="info" fontWeight="bold">
                        {formatCurrency(grandTotal, currency)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                );
              })()}
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton
            onClick={() => setOpenEditInvoiceModal(false)}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveInvoice}
            variant="gradient"
            color="info"
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
      <SendPaymentLinkModal
        open={isPaymentLinkModalOpen}
        onClose={closePaymentLinkModal}
        invoiceId={paymentLinkInvoiceId || invoice?.id}
        defaultEmail={travelOrderDetail?.email || ''}
        title={paymentLinkModalTitle}
        updateSnackbar={setSnackbar}
        onSent={() => {
          setSnackbar({ open: true, message: 'Payment link email sent.', severity: 'success' });
          fetchInvoiceDetail();
        }}
      />
      <EmailModal
        open={isEmailModalOpen}
        onClose={handleToggleEmailModal}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        handleSend={handleSendEmail}
        isGenerating={false}
        errorMessage={''}
        emailContext={emailContext}
        setEmailContext={setEmailContext}
        showCc={showCc}
        showBcc={showBcc}
        toggleCc={toggleCc}
        toggleBcc={toggleBcc}
        handleQuillChange={handleQuillChange}
        modules={{}}
        formats={[]}
        loading={isEmailLoading}
        attachments={emailAttachments}
        currentAttachment={currentAttachment}
        setEmailAttachments={setEmailAttachments}
        isSending={isEmailSending}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {renderGSTDialog()}
      {renderCancelDialog()}
      <AttachmentUploadModal
        isOpen={isAttachmentModalOpen}
        onClose={() => setIsAttachmentModalOpen(false)}
        onUpload={handleUploadSuccess}
        axiosInstance={axiosInstance}
        maxSizeInMB={5}
      />

      {/* Feed Item Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="feed-delete-modal-title"
        aria-describedby="feed-delete-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <MDTypography
              id="feed-delete-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              Delete Message
            </MDTypography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            <MDTypography
              id="feed-delete-modal-description"
              variant="body1"
              mb={3}
              sx={{ color: '#34495e' }}
            >
              Are you sure you want to delete this message? This action cannot be undone.
            </MDTypography>

            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton
                onClick={handleDeleteCancel}
                variant="outlined"
                color="secondary"
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleDeleteConfirmOk}
                variant="gradient"
                color="error"
              >
                Delete
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>

      
      <MDBox>
        {error && <MDTypography color="error">{error}</MDTypography>}
      </MDBox>
    </DashboardLayout>
  );
}

export default InvoiceDetail;